/**
 * PrimeReact 11, in the shapes this app already used.
 *
 * v11 made almost every component compositional: Panel, Fieldset, Toolbar,
 * Card, Chip, DataTable, DataView, Timeline, Tree and DatePicker are now sets
 * of parts you assemble, and Column and Calendar do not exist at all. Written
 * out at each call site that is three to fifteen extra tags per screen, thirty
 * six of them for Panel and Fieldset alone, all of it the same tags in the
 * same order.
 *
 * So the assembly lives here once. Every export takes the props its v9
 * counterpart took, which keeps the screens reading as they always did and
 * keeps the whole of what v11 changed in one file to correct when it changes
 * again. The parts each of these assembles were verified by rendering them --
 * see test/EsPrimeProbe.test.jsx and EsPrimeProbe2.test.jsx, which exist
 * because the package ships types rather than documentation.
 *
 * PrimeReact's own Button, InputText and Divider keep their v11 shape and are
 * still imported directly. Nothing here wraps them.
 *
 * <p><b>data-testid is an explicit prop on every export here</b>, added
 * 2026-09-05 when the cucumber suite moved onto aquifer, whose vocabulary is
 * data-testid-first -- `user clicks id "es-campaign-join-button"` resolves to
 * [data-testid='es-campaign-join-button'] and needs no per-app glue. Explicit
 * rather than a spread of the rest, because that is this file's rule
 * everywhere else: a prop this file passes is a prop verified to reach the
 * DOM. Probed in test/EsPrimeProbe8.test.jsx -- every v11 part forwards an
 * unknown attribute to the element it renders, so the only thing that ever
 * swallowed the attribute was the prop list here, and a screen that wrote
 * data-testid on an Es* wrapper rendered nothing at all. That is the worst
 * shape a test hook can have: the feature file reads correctly and fails.
 *
 * <p>Where a wrapper GENERATES its children -- tree nodes, table rows, tabs,
 * stepper steps, paginator pages, timeline events, meter segments -- the
 * caller cannot reach them, so each derives its own id from the one it was
 * given: `<id>-node-<key>`, `<id>-row-<key>`, `<id>-tab-<value>` and so on.
 * The suffix each wrapper uses is named in its comment.
 *
 * The second half of this file is the INPUTS, added for characters. Everything
 * above was a display component -- the app had no form control of its own but
 * EsDatePicker, because until now every screen either read or was an admin CRUD
 * form built out of bare InputText. A character sheet is nothing but inputs.
 *
 * They all follow the EsDatePicker rule: v11 reports a change as
 * onValueChange( { value } ) or onCheckedChange( { checked } ), and each wrapper
 * reshapes that into the synthetic { target: { name, value } } the app's
 * handleChange functions already read, so no screen learns a second convention.
 *
 * Everything comes from @primereact/ui, NOT from primereact. In v11 the
 * `primereact` package is the unstyled primitives -- its own README says
 * "styling is left entirely open" and "no theme or preset is needed, since
 * primitives ship no CSS". Built on those, the app rendered correct markup with
 * class="" throughout and no chrome at all: cards with no card, tables with no
 * table, a timeline with no line. @primereact/ui is the same parts with themed
 * Root and Content wrappers, and is what the theme preset actually styles.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete } from '@primereact/ui/autocomplete';
import { Button } from '@primereact/ui/button';
import { Card } from '@primereact/ui/card';
import { Checkbox } from '@primereact/ui/checkbox';
import { Chip } from '@primereact/ui/chip';
import { DataTable } from '@primereact/ui/datatable';
import { DataView } from '@primereact/ui/dataview';
import { DatePicker } from '@primereact/ui/datepicker';
import { Dialog } from '@primereact/ui/dialog';
import { Fieldset } from '@primereact/ui/fieldset';
import { FileUpload } from '@primereact/ui/fileupload';
import { IconField } from '@primereact/ui/iconfield';
import { InputColor, parseColor } from '@primereact/ui/inputcolor';
import { InputNumber } from '@primereact/ui/inputnumber';
import { Menu, useMenuContext } from '@primereact/ui/menu';
import { MeterGroup } from '@primereact/ui/metergroup';
import { Paginator } from '@primereact/ui/paginator';
import { Panel } from '@primereact/ui/panel';
import { Popover } from '@primereact/ui/popover';
import { Select } from '@primereact/ui/select';
import { Slider } from '@primereact/ui/slider';
import { Splitter } from '@primereact/ui/splitter';
import { Stepper } from '@primereact/ui/stepper';
import { Tabs } from '@primereact/ui/tabs';
import { Textarea } from '@primereact/ui/textarea';
import { Timeline } from '@primereact/ui/timeline';
import { ToggleButton } from '@primereact/ui/togglebutton';
import { ToggleButtonGroup } from '@primereact/ui/togglebuttongroup';
import { Toolbar } from '@primereact/ui/toolbar';
import { Tooltip } from '@primereact/ui/tooltip';
import { Tree } from '@primereact/ui/tree';
import { useSplitter } from '@primereact/headless/splitter';
import { esFocusBlankOnMount } from './EsFocusBlank';
import { useEsStrings } from './EsStrings';

/**
 * ⚠️ EVERY PART BELOW THAT RENDERS A BUTTON CARRIES type="button".
 *
 * A <button> with no type attribute is type="submit". That is the HTML
 * default and it has nothing to do with PrimeReact -- but every trigger,
 * toggle and pager button in this file is a bare button, so any of them
 * placed inside a <form> submits it.
 *
 * <b>What that looked like.</b> Opening the group dropdown on the mailing form
 * saved the mailing and navigated to the list: the picker appeared for the
 * instant before the whole screen went away, so the report was "it closes the
 * form before i can select" rather than anything about a submit. Reported
 * 2026-09-18. EsDatePicker had it too -- the talk form's calendar button has
 * been inside a <form> since it was written.
 *
 * It is stated on ALL of them rather than on the two that were caught, because
 * which parts happen to sit inside a form today is not a property of this file
 * and the next one costs nothing to get right. A type on a part that is not a
 * button is inert.
 *
 * Asserted in test/EsMailingForm.test.jsx ("opening it does not save or
 * navigate") and test/EsPrimeFormSubmit.test.jsx, which covers the class
 * rather than the instance.
 */
export function EsPanel( { className, children, 'data-testid': dataTestId } ) {
    return (
        <Panel.Root className={ className } data-testid={ dataTestId }>
            <Panel.Content>{ children }</Panel.Content>
        </Panel.Root>
    );
}

export function EsFieldset( { className, children, 'data-testid': dataTestId } ) {
    return (
        <Fieldset.Root className={ className } data-testid={ dataTestId }>
            <Fieldset.Content>{ children }</Fieldset.Content>
        </Fieldset.Root>
    );
}

export function EsToolbar( { className, start, center, end, 'data-testid': dataTestId } ) {
    return (
        <Toolbar.Root className={ className } data-testid={ dataTestId }>
            <Toolbar.Start>{ start }</Toolbar.Start>
            <Toolbar.Center>{ center }</Toolbar.Center>
            <Toolbar.End>{ end }</Toolbar.End>
        </Toolbar.Root>
    );
}

/**
 * Title and subtitle go in the Caption INSIDE the Body, not in the Header.
 *
 * v11's padding token is --p-card-body-padding and it applies to Body; Header
 * has none. With the title in the Header it sat flush against the card's left
 * edge instead of inset the way v9's <Card title=...> rendered it. The token
 * set says as much -- there is a --p-card-caption-gap for exactly this pair.
 */
export function EsCard( { title, subTitle, className, children, 'data-testid': dataTestId } ) {
    return (
        <Card.Root className={ className } data-testid={ dataTestId }>
            <Card.Body>
                { ( title || subTitle ) && (
                    <Card.Caption>
                        { /* The title is its own hook -- `<id>-title` -- because a card
                             is usually asserted on by the heading it carries. */ }
                        { title && <Card.Title data-testid={ dataTestId && `${ dataTestId }-title` }>
                            { title }</Card.Title> }
                        { subTitle && <Card.Subtitle>{ subTitle }</Card.Subtitle> }
                    </Card.Caption>
                ) }
                <Card.Content>{ children }</Card.Content>
            </Card.Body>
        </Card.Root>
    );
}

/**
 * An input with an icon inside it.
 *
 * v9 did this with the utility class p-input-icon-left wrapped round the input.
 * That class does not exist in v11; IconField is the component that replaced it,
 * and the icon's side comes from where the Inset sits in the markup rather than
 * from the class name.
 */
export function EsIconField( { className, icon, children, 'data-testid': dataTestId } ) {
    return (
        <IconField.Root className={ className } data-testid={ dataTestId }>
            <IconField.Inset>{ icon }</IconField.Inset>
            { children }
        </IconField.Root>
    );
}

// style, because a tag's colour is computed from its name at run time and
// Tailwind emits no class for a value it cannot read in the source.
export function EsChip( { label, className, style, 'data-testid': dataTestId } ) {
    return (
        <Chip.Root className={ className } style={ style } data-testid={ dataTestId }>
            <Chip.Label>{ label }</Chip.Label>
        </Chip.Root>
    );
}

/**
 * A stacked bar of measurements that share one total.
 *
 * New in this app -- there was no v9 counterpart to preserve, so this takes
 * v11's own props rather than an older signature. `value` is a list of
 * { key, label, value, color }; `max` is the total the segments are shares of.
 *
 * Three things about the v11 composition, all read out of node_modules rather
 * than guessed, because MeterGroup ships types and no prose:
 *
 * 1. **`max` is not optional in practice.** useMeterGroup defaults min 0 and
 *    max 100, and MeterGroup.Meter sets its own width to
 *    percentAsString( value ) against that range. Left at the default, a
 *    segment of 339 out of 1,203 renders at 100% and eats the bar. Every
 *    caller has to say what the whole is.
 * 2. **The percentage is rounded** -- percent() is Math.round -- so anything
 *    under half a percent comes out 0% and is not drawn at all. That is a fact
 *    about the component the CALLER has to design around by bucketing its own
 *    tail; there is nothing to fix here.
 * 3. **Marker and Text carry no colour or content of their own.** Marker takes
 *    a `color` and paints its background from it; the text is whatever the
 *    caller renders. Hence labelTemplate, which is handed the whole item so a
 *    label can be a link, a count, or both -- the segments on the codex portal
 *    are clickable, and a legend that only prints words would be the same
 *    information with the way in removed.
 *
 * The legend is only assembled when labelTemplate is given: MeterGroup.Labels
 * is an <ol> and Label an <li>, and an empty one is a stray list in the
 * accessibility tree.
 */
export function EsMeterGroup( { value, max, className, labelTemplate, 'data-testid': dataTestId } ) {
    const aList = Array.isArray( value ) ? value : [];
    const itemKey = ( pItem, pIndex ) => pItem.key ?? pItem.label ?? pIndex;
    // Segments and legend rows are generated, so they derive their own ids:
    // <id>-meter-<key> and <id>-label-<key>.
    const itemTestId = ( pPart, pItem, pIndex ) => dataTestId
        && `${ dataTestId }-${ pPart }-${ itemKey( pItem, pIndex ) }`;

    return (
        <MeterGroup.Root className={ className } max={ max } data-testid={ dataTestId }>
            <MeterGroup.Meters>
                { aList.map( ( pItem, pIndex ) => (
                    <MeterGroup.Meter key={ itemKey( pItem, pIndex ) }
                                      value={ pItem.value }
                                      color={ pItem.color }
                                      data-testid={ itemTestId( 'meter', pItem, pIndex ) } />
                ) ) }
            </MeterGroup.Meters>
            { labelTemplate && (
                <MeterGroup.Labels>
                    { aList.map( ( pItem, pIndex ) => (
                        <MeterGroup.Label key={ itemKey( pItem, pIndex ) }
                                          data-testid={ itemTestId( 'label', pItem, pIndex ) }>
                            <MeterGroup.Marker color={ pItem.color } />
                            <MeterGroup.Text>{ labelTemplate( pItem ) }</MeterGroup.Text>
                        </MeterGroup.Label>
                    ) ) }
                </MeterGroup.Labels>
            ) }
        </MeterGroup.Root>
    );
}

/**
 * A column of an EsTable. Renders nothing itself: EsTable reads these off its
 * children for their props, the way DataTable read Column in v9, so that a
 * table still reads as a list of columns rather than a pair of nested loops.
 */
export function EsColumn() {
    return null;
}

/**
 * The v9 DataTable call: a value and a set of columns.
 *
 * `body` keeps its old signature, ( data, props ) with props.rowIndex, because
 * every actions column in this app reaches for props.rowIndex to find its row.
 */
export function EsTable( { className, value, tableStyle, children, rowTestId, size,
                           'data-testid': dataTestId } ) {
    const rowList = Array.isArray( value ) ? value : [];
    const columnList = React.Children.toArray( children )
        .filter( ( child ) => React.isValidElement( child ) && child.type === EsColumn )
        .map( ( child ) => child.props );

    const columnKey = ( column, index ) => column.field || column.header || index;

    // Rows and cells are generated here, so they derive their own ids from the
    // table's: <id>-row-<row.id or index> and <id>-cell-<row key>-<column key>.
    // The row key is the entity id wherever there is one, so a feature file can
    // address the row it created rather than counting positions.
    //
    // The column segment is kebabed rather than used raw. columnKey falls back
    // to the HEADER when a column has no field -- which is every actions column
    // in this app -- so "Hit Points" would put a capital and a space inside a
    // test id, and es-x-cell-7-Hit Points is not a selector anyone can write.
    // The React key above keeps the raw value; only the id is normalised.
    const rowKey = ( row, rowIndex ) => row.id ?? rowIndex;

    // A caller whose rows already have ids a feature file can spell -- the
    // campaign roster keys its seats by display name, because a member id is
    // minted by the database -- names them itself rather than inheriting -row-.
    const rowId = ( row, rowIndex ) => ( rowTestId
        ? rowTestId( row, rowIndex )
        : dataTestId && `${ dataTestId }-row-${ rowKey( row, rowIndex ) }` );
    const columnSlug = ( column, index ) => String( columnKey( column, index ) )
        .toLowerCase().replace( /[^a-z0-9]+/g, '-' ).replace( /^-|-$/g, '' );
    const cellTestId = ( row, rowIndex, column, index ) => dataTestId
        && `${ dataTestId }-cell-${ rowKey( row, rowIndex ) }-${ columnSlug( column, index ) }`;

    return (
        <DataTable.Root className={ className } data={ rowList } dataKey="id" size={ size }
                        data-testid={ dataTestId }>
            <DataTable.Table style={ tableStyle }>
                <DataTable.THead>
                    <DataTable.THeadRow>
                        { columnList.map( ( column, index ) => (
                            <DataTable.THeadCell key={ columnKey( column, index ) }
                                                 data-testid={ dataTestId
                                                     && `${ dataTestId }-head-${ columnSlug( column, index ) }` }>
                                { column.header }
                            </DataTable.THeadCell>
                        ) ) }
                    </DataTable.THeadRow>
                </DataTable.THead>
                <DataTable.TBody>
                    { rowList.map( ( row, rowIndex ) => (
                        <DataTable.Row key={ rowKey( row, rowIndex ) } index={ rowIndex }
                                       data-testid={ rowId( row, rowIndex ) }>
                            { columnList.map( ( column, index ) => (
                                <DataTable.Cell key={ columnKey( column, index ) }
                                                data-testid={ cellTestId( row, rowIndex, column, index ) }>
                                    { column.body
                                        ? column.body( row, { rowIndex } )
                                        : row[ column.field ] }
                                </DataTable.Cell>
                            ) ) }
                        </DataTable.Row>
                    ) ) }
                </DataTable.TBody>
            </DataTable.Table>
        </DataTable.Root>
    );
}

/**
 * The paginator, in v11's own terms rather than v9's.
 *
 * This one is NOT a v9 signature. The rest of this file preserves the old
 * calls because their call sites are everywhere; the paginator had no call
 * sites left to preserve -- v11's `Paginator` off @primereact/ui is a namespace
 * of parts, and rendering it as a component is React error #130, "got: object",
 * which is what took the changelog down. So it takes v11's props: a 1-based
 * page, a total, a page size, and a callback given the page number.
 *
 * Paginator.Pages IS a generator, whatever an earlier reading of it here said.
 * Its function children are handed the Pages instance, and paginator.pages is
 * the windowed list v11 computes from siblings, edges and showEllipsis: for ten
 * pages on page three it is 1 2 3 4 5 … 10, not all ten. Listing every page by
 * hand instead -- which is what v9 had no choice about -- throws that away and
 * gets worse the more releases there are. See test/EsPrimeProbe2.test.jsx.
 *
 * The four nav buttons render empty without children: v11 ships no default
 * icon, and the theme does not add one. The icons are PrimeIcons, already
 * bundled and already used elsewhere in the app.
 */
export function EsPaginator( {
    page = 1, total = 0, itemsPerPage = 10, className, onPageChange,
    'data-testid': dataTestId,
} ) {
    // Every button here is generated -- the four nav buttons by this file, the
    // numbered ones by v11's own windowing -- so they derive their ids:
    // <id>-first, <id>-prev, <id>-next, <id>-last and <id>-page-<number>.
    const partTestId = ( pPart ) => dataTestId && `${ dataTestId }-${ pPart }`;

    // defaultPage as well as page: the headless pager seeds its own state from defaultPage,
    // 1 unless given, and catches up to page only in an effect a commit after it mounts.
    return (
        <Paginator.Root
            className={ className }
            data-testid={ dataTestId }
            defaultPage={ page }
            page={ page }
            total={ total }
            itemsPerPage={ itemsPerPage }
            onPageChange={ ( event ) => onPageChange && onPageChange( event.value ) }
        >
            { /* Content and Pages carry ids of their own for a reason that is
                 not tidiness. <b>Paginator is the ONE component in this file
                 that copies an unknown prop from its Root onto every part
                 underneath it</b> -- probed in EsPrimeProbe8: with the id on
                 the Root alone, eight elements answered to it, the nav and
                 every button inside. The other nineteen wrappers here put it
                 on exactly one element. A part that names itself wins the
                 merge, so naming all of them is what makes the ids unique
                 again -- leave one unnamed and it silently answers to the
                 paginator's own id. */ }
            <Paginator.Content data-testid={ partTestId( 'content' ) }>
                <Paginator.First type="button" data-testid={ partTestId( 'first' ) }>
                    <i className="pi pi-angle-double-left" /></Paginator.First>
                <Paginator.Prev type="button" data-testid={ partTestId( 'prev' ) }>
                    <i className="pi pi-angle-left" /></Paginator.Prev>
                <Paginator.Pages data-testid={ partTestId( 'pages' ) }>
                    { ( { paginator } ) => paginator.pages.map( ( item, index ) => (
                        item.type === 'ellipsis'
                            ? <Paginator.Ellipsis key={ `gap-${ index }` }
                                                  data-testid={ partTestId( `gap-${ index }` ) }>
                                &hellip;</Paginator.Ellipsis>
                            /* Page renders its own number: children ?? value. */
                            : <Paginator.Page key={ item.value } value={ item.value }
                                              data-testid={ partTestId( `page-${ item.value }` ) } />
                    ) ) }
                </Paginator.Pages>
                <Paginator.Next type="button" data-testid={ partTestId( 'next' ) }>
                    <i className="pi pi-angle-right" /></Paginator.Next>
                <Paginator.Last type="button" data-testid={ partTestId( 'last' ) }>
                    <i className="pi pi-angle-double-right" /></Paginator.Last>
            </Paginator.Content>
        </Paginator.Root>
    );
}

/**
 * The v9 DataView call.
 *
 * v11's DataView has no paginator of its own -- the parts are Root, Content,
 * Header, Footer and Empty, and nothing else -- so paging is done here and
 * driven by the separate Paginator component. Its page is 1-based.
 *
 * `startPage` is which page to OPEN on, for a list that should not open at the
 * top -- the talks page opens on its last page, where the newest talks are. It is
 * a starting point and not a controlled value: the reader's own paging wins
 * from the first click, and the list re-seats only when the caller computes a
 * DIFFERENT start, which for a list loading asynchronously is the moment its
 * data arrives. Callers that pass nothing get page 1 and never re-seat.
 *
 * <p>The re-seat happens during the render that brings the new start, not in an
 * effect after it: an effect committed one render on the old page first, so the
 * talks page drew talks 1-10 under a pager that then mounted on page 1 and
 * stayed there a commit behind the rows.
 */
export function EsDataList( {
    id, value, itemTemplate, paginator, paginatorPosition = 'bottom', rows = 10,
    startPage = 1, 'data-testid': dataTestId,
} ) {
    const itemList = Array.isArray( value ) ? value : [];
    const [ page, setPage ] = useState( startPage );
    const [ seatedAt, setSeatedAt ] = useState( startPage );
    if ( seatedAt !== startPage ) {
        setSeatedAt( startPage );
        setPage( startPage );
    }

    const pageCount = Math.max( 1, Math.ceil( itemList.length / rows ) );
    const current = Math.min( page, pageCount );

    const shown = paginator
        ? itemList.slice( ( current - 1 ) * rows, current * rows )
        : itemList;

    const above = paginatorPosition === 'top' || paginatorPosition === 'both';
    const below = paginatorPosition !== 'top';

    // <id>-paginator for the pager, <id>-item-<key> for each row -- the row's
    // own id or codex key, never its position on the page, since the whole
    // point of the key fallback below is that positions move.
    //
    // TWO PAGERS MEANS TWO NAMES, and this is a suffix rather than one shared
    // id for a reason worth stating. At paginatorPosition="both" this component
    // renders the SAME pager above and below the list, and EsPaginator derives
    // nine ids of its own from whatever it is handed -- -paginator, -content,
    // -first, -prev, -next, -last, -pages and one per page button. Handing both
    // copies the same base made every one of those nine resolve to two
    // elements, which is not a broken render but IS an ambiguous selector: a
    // step that clicks "next" would act on whichever the driver found first,
    // and a step that asserted one was displayed could be satisfied by the
    // other. So `both` gets -paginator-top and -paginator-bottom, and the
    // single-pager case keeps the plain -paginator it has always had, because
    // there is nothing to disambiguate and renaming it would break every caller
    // that already addresses it.
    const pagerFor = ( pSuffix ) => paginator && itemList.length > rows ? (
        <EsPaginator
            page={ current }
            total={ itemList.length }
            itemsPerPage={ rows }
            onPageChange={ setPage }
            data-testid={ dataTestId && `${ dataTestId }-paginator${ pSuffix }` }
        />
    ) : null;

    const bothEnds = above && below;

    return (
        <DataView.Root id={ id } data-testid={ dataTestId }>
            { above && pagerFor( bothEnds ? '-top' : '' ) }
            <DataView.Content>
                { /* key on the codex's own key before falling back to the index.
                     A codex summary row (contract §1) has a `key` and no `id` --
                     it is a projection, not the entity -- so every row in a
                     paginated codex list was keyed by its position. React then
                     reuses the DOM of row 3 of page 1 for row 3 of page 2, which
                     is harmless for text and is not harmless the moment a row
                     holds anything with state of its own. */ }
                { shown.map( ( item, index ) => (
                    <div key={ item.id ?? item.key ?? index }
                         data-testid={ dataTestId
                             && `${ dataTestId }-item-${ item.id ?? item.key ?? index }` }>
                        { itemTemplate( item ) }</div>
                ) ) }
            </DataView.Content>
            { below && pagerFor( bothEnds ? '-bottom' : '' ) }
        </DataView.Root>
    );
}

export function EsTimeline( { value, align, className, marker, content,
    'data-testid': dataTestId } ) {
    const eventList = Array.isArray( value ) ? value : [];
    // Generated, so derived: <id>-event-<event id or index>.
    const eventTestId = ( item, index ) => dataTestId
        && `${ dataTestId }-event-${ item.id ?? index }`;

    // Marker and Content on their own are not a timeline: without a Separator
    // wrapping the marker, and a Connector between events, v11 renders the
    // markers stacked down the middle with no line and no lane for the cards.
    // Opposite is the empty half of each alternating row and has to be present
    // for align="alternate" to have anything to alternate.
    return (
        <Timeline.Root align={ align } className={ className } data-testid={ dataTestId }>
            { eventList.map( ( item, index ) => (
                <Timeline.Event key={ item.id ?? index }
                                data-testid={ eventTestId( item, index ) }>
                    <Timeline.Opposite />
                    <Timeline.Separator>
                        <Timeline.Marker>{ marker && marker( item ) }</Timeline.Marker>
                        { index < eventList.length - 1 && <Timeline.Connector /> }
                    </Timeline.Separator>
                    <Timeline.Content>{ content && content( item ) }</Timeline.Content>
                </Timeline.Event>
            ) ) }
        </Timeline.Root>
    );
}

/**
 * What v9 called Tree.
 *
 * Two things about the v11 composition, both found by probe (see
 * test/EsPrimeProbe2.test.jsx), both of which broke "click a name in the codex
 * sidebar and show that entry":
 *
 * 1. Tree.Content is not decoration, it is the clickable row. The headless node
 *    builds contentProps carrying onClick -> onNodeClick -> onSelectionChange,
 *    and Tree.Content is the only part that merges those props onto an element.
 *    A node of Toggle + Label alone -- what this was -- renders a row with no
 *    click handler at all, so nothing fired and the <li> stayed
 *    aria-selected="false". Tree.Selection is no alternative: its `mode` prop
 *    is dropped before setup() reads it, so neither its checkbox nor its radio
 *    UI is reachable. Content is the selection path and is what the theme
 *    styles (.p-tree-node-content carries the colour, hover and selected paint).
 *    Tree.Toggle stops its own click, so expanding a branch does not select it.
 *
 * 2. The event is not the v9 key. v9 handed the callback the selected key as a
 *    string; v11 hands back TreeSelectionKeys, a map -- { beta: true } in single
 *    mode, and {} when the already-selected node is clicked again. EsCodexNavToc
 *    does navigate( VIEW_CODEX + "/" + e.value ), which under the map produced
 *    /view/codex/[object Object].
 *
 * So the event is reshaped here rather than at every call site, the same way
 * EsDatePicker below reshapes onValueChange back into an onChange the forms
 * already understand. Callers keep reading e.value as the key string. The empty
 * map is swallowed instead of forwarded, or re-clicking the current entry would
 * navigate to /view/codex/undefined -- the same bug in a new coat.
 *
 * selectionKeys is passed through for the third part of the same bug. v11 keeps
 * selection internally when the prop is absent, and the codex sidebar outlives
 * navigation: Previous, Next and the search results all move the entry without
 * telling the tree. The tree then still believes the old node is selected, so
 * clicking that node is read as a deselect, comes back as {}, and the click
 * does nothing at all. A caller that owns the selection -- EsCodexNavToc drives
 * it from the URL -- stays in step and highlights the entry actually on screen.
 * useControlledState still calls onChange when the prop is present; only its
 * internal setState is skipped, so navigation keeps firing.
 */
export function EsTree( {
    value, className, expandedKeys, selectionKeys, selectionMode, onSelectionChange,
    onExpandedChange, nodeAction, draggable, dragScope, validateMove, onMove, 'data-testid': dataTestId,
} ) {
    const handleSelectionChange = ( event ) => {
        const aKeys = Object.keys( event.value ?? {} );

        if ( onSelectionChange && aKeys.length ) {
            onSelectionChange( { originalEvent: event.originalEvent, value: aKeys[ 0 ] } );
        }
    };

    // Expansion reports the same way selection does: an event carrying the whole
    // new keys map. The caller handed expandedKeys in as a map, so it gets one
    // back rather than learning a second convention.
    //
    // Without this the prop was controlled with nothing to update it, and
    // useControlledState never updates a controlled value by itself -- so the
    // tree was frozen at whatever map it was first given and no branch could be
    // opened. Probed: with expandedKeys={{}} and no handler, clicking the toggle
    // fires once and the child stays hidden.
    //
    // The base type calls this field `node`; the object v11 actually emits has
    // only `value`. Read the runtime, not UseTreeExpandedChangeEvent.
    const handleExpandedChange = ( event ) => {
        if ( onExpandedChange ) onExpandedChange( event.value ?? {} );
    };

    return (
        <Tree.Root
            className={ className }
            data-testid={ dataTestId }
            value={ value }
            expandedKeys={ expandedKeys }
            onExpandedChange={ handleExpandedChange }
            selectionKeys={ selectionKeys }
            selectionMode={ selectionMode }
            onSelectionChange={ handleSelectionChange }
            /* v11's own drag and drop, when the caller wants it: `draggable` on the
                 Root turns it on, a node's own `draggable: false` keeps that node
                 still, and a node's `droppable: false` refuses a drop INSIDE it while
                 before and after stay open. validateMove vetoes a move and onMove
                 reports one; the tree's value is the caller's, so nothing moves on
                 screen until the caller reloads it. All three in useTree's
                 defaultProps, checked in node_modules/@primereact/headless/tree.

                 dragScope, a string, fences one tree off from another's lines: v11's
                 drag is page-wide, and a tree takes a drop only from a drag that began
                 in its own scope. A line from outside draws no drop line and the
                 browser shows the drop refused. */
            draggable={ draggable }
            droppable={ draggable }
            draggableScope={ dragScope }
            droppableScope={ dragScope }
            validateMove={ validateMove }
            onMove={ onMove }
        >
            <Tree.Nodes>
                { ( row ) => (
                    <Tree.Node uKey={ row.node.key }>
                        { /* The line a drop would land on. Renders nothing until a
                             drag hovers the row's top or bottom quarter. */ }
                        { draggable && <Tree.DropIndicator position="before" /> }
                        { /* Content, not Node: Content is the row that carries the
                             click handler (see above), so it is the element a
                             feature file means when it says "click the node".
                             Derived as <id>-node-<node key>. */ }
                        <Tree.Content data-testid={ dataTestId
                            && `${ dataTestId }-node-${ row.node.key }` }>
                            { /* Toggle renders EMPTY without a child -- the same thing v11
                                 does to Paginator.First/Prev/Next/Last, and for the same
                                 reason: the parts ship no icons. Two consequences, and the
                                 second is the one that got reported. There was no chevron at
                                 all, so nothing on screen said a branch could be folded; and
                                 an empty button does not fill the box a leaf's
                                 .p-tree-node-toggle-spacer occupies, so root PARENTS sat
                                 left of root LEAVES -- "Spells" looked indented against
                                 "Classes" and "Species" when all three are level 1. Probed:
                                 every toggle came back with innerHTML "" and 0 children.
                                 Tree.ToggleIndicator is NOT the answer, though it looks like
                                 it: it has no rule anywhere in @primereact/styles/tree and
                                 renders an empty span, so it fills the button with nothing.
                                 The icon is ours, as it is in EsPaginator. The render prop
                                 hands over `leaf` and `expanded`, so the arrow is chosen here
                                 rather than rotated by a CSS rule keyed on data-expanded --
                                 a leaf gets no arrow at all, only v11's spacer. */ }
                            <Tree.Toggle type="button">
                                { !row.leaf && <i className={ row.expanded
                                    ? 'pi pi-chevron-down' : 'pi pi-chevron-right' } /> }
                            </Tree.Toggle>
                            <Tree.Label>{ row.node.label }</Tree.Label>
                            { /* Anything the caller wants ON the row, after the
                                 label -- a count, a control, both. It is a render
                                 prop rather than a fixed slot because a tree is a
                                 tree: the admin codex rail hangs an entry count and
                                 an edit button here, and the codex sidebar a reader
                                 sees hangs nothing at all, and neither should have
                                 to know about the other.

                                 Handed the NODE and not the row, because a caller
                                 acts on the thing the node represents; `leaf` and
                                 `expanded` are this component's business. */ }
                            { nodeAction && nodeAction( row.node ) }
                        </Tree.Content>
                        { /* v11 draws the rows flat, so "after" an open parent sits
                             between it and its first child. It is drawn a level in, and
                             a caller's onMove reads it as the first child, which is
                             where the reader sees the line. --es-tree-drop-level is
                             that level; the stylesheet indents by it. */ }
                        { draggable && <Tree.DropIndicator position="after" style={ {
                            '--es-tree-drop-level': row.level + ( row.expanded && row.node.children?.length ? 1 : 0 ),
                        } } /> }
                    </Tree.Node>
                ) }
            </Tree.Nodes>
        </Tree.Root>
    );
}

/**
 * What v9 called Calendar.
 *
 * The forms that use it share one handleChange reading event.target.name and
 * event.target.value, so the v11 onValueChange event is reshaped into that
 * rather than every form learning a second convention. `event.value` is a Date
 * in single mode, which is what EsTalkForm and EsCommentForm already put in
 * their state and hand to JSON.stringify -- the reshaping is the whole of what
 * this wrapper owes them and it has not changed.
 *
 * <p><b>Until 2026-09-04 this could not pick a date.</b> It ended at
 * &lt;DatePicker.Calendar /&gt; with no children, and Calendar renders
 * props.children and nothing else -- the same "the parts ship no content" rule
 * as Tree.Toggle and Paginator.First/Prev/Next/Last, in the one place where it
 * removed the entire point of the component. The popup opened on a month/year
 * header over an empty box. Two more parts were missing with it; all three are
 * below.
 *
 * <p><b>1. The day grid is Table + TableHead + TableBody, and the last two
 * GENERATE.</b> Table is a dumb slot -- a &lt;table&gt; carrying the class for
 * whichever view is current -- but neither of the others needs children, and
 * neither should be given any:
 *
 * <ul>
 * <li>TableHead falls back to a TableHeadRow of one TableHeadCell +
 *     TableHeadWeekLabel per entry in the instance's `weekDays`, so the
 *     Su..Sa strip is v11's, localised, and free.</li>
 * <li>TableBody falls back to `getIndexedMonth( index ).dates` -- the real
 *     five-or-six week grid, leading and trailing days of the neighbouring
 *     months included -- as TableBodyRow / TableBodyCell, and TableBodyCell
 *     falls back to a Day, and Day falls back to the day number. Writing that
 *     map out here is the v9 shape and would throw away the month arithmetic,
 *     the other-month padding, the selectable flags and the week numbers.</li>
 * </ul>
 *
 * <p><b>THREE TableBodys, one per view.</b> TableBody renders null unless the
 * instance's currentView equals its own `view` prop, and SelectMonth /
 * SelectYear in the header do nothing but move currentView. With only the
 * date body assembled, clicking the month name empties the panel -- the
 * original bug again, one click away. TableHeadRow likewise returns null
 * outside the date view, so the weekday strip disappears by itself and needs
 * no condition here.
 *
 * <p><b>2. Positioner, which is not decoration.</b> It is the part that carries
 * pIf={ rendered } and the part that runs usePositioner, so without it the
 * panel is (a) never positioned -- it lands at the top-left of the page rather
 * than under the field -- and (b) NEVER REMOVED. Popup's motion runs its leave
 * transition and calls setRendered( false ), and with nothing reading that flag
 * the closed panel simply stays in the document. Probed: with Portal > Popup
 * the panel is still there long after a day is clicked; with the Positioner
 * between them it goes. Select has the same three-part nesting for the same
 * reason.
 *
 * <p><b>3. Trigger, Prev and Next ship no icons</b>, the same as every other
 * button in this file. All three rendered as empty boxes.
 *
 * <p>Body is the flex container v11 lays multiple months out in
 * (.p-datepicker-calendar-container). With numberOfMonths at its default of 1
 * it is one calendar in a flex row, but it is where a second month would go and
 * where the group border/gap tokens are addressed, so the calendar is placed
 * inside it rather than hung off the Panel.
 *
 * <p>The closing is ASYNCHRONOUS -- Popup animates out -- so a test that clicks
 * a day and looks for the panel in the same tick still finds it. Assert its
 * disappearance inside waitFor.
 *
 * <p><b>4. The text field was blank on the first paint, and the clone below is
 * what fixes it.</b> This is a defect in useDatePicker 11.1.0 -- the newest
 * release there is, so there is nothing to upgrade to -- and it is worth
 * writing down in full, because none of the obvious repairs touch it.
 *
 * <p>Two bindings in useDatePicker's setup are read before they are
 * initialised. The one that matters here is the resolved format,
 * `useMemo( () =&gt; props.dateFormat || locale.dateFormat )`, which is declared
 * roughly 170 statements AFTER the memo that formats the value for the input
 * (`inputFieldValue`). Both live in the same `let a = ..., b = ...` chain, so
 * every call of setup builds a fresh temporal dead zone and the formatting memo
 * throws into it EVERY render, not merely the first. The throw is swallowed by
 * the format function's own try/catch into '', so `inputFieldValue` -- which is
 * the input's `defaultValue`, and also what DatePicker.Value renders -- is
 * permanently the empty string. (The second dead binding is the string parser:
 * render the Root with a STRING value and the useState initialiser reaches it
 * on a path with no try/catch, and the ReferenceError escapes verbatim. We
 * never pass a string, so it only shows up in a probe.)
 *
 * <p>The only thing that ever puts text in the field is a separate effect keyed
 * on [ props.value ], which formats from an event-time closure -- where the
 * binding IS initialised -- and assigns straight to the input's DOM node. That
 * effect cannot work on mount: it writes to `popover.state.anchorElement`, and
 * the anchor is React STATE set from a ref callback, so it is still null when
 * the mount effects run and is only populated one render later.
 *
 * <p>So the field filled in on an Edit screen, where the fetched record
 * replaces the value and re-runs that effect with an anchor in place, and
 * stayed blank on New Talk and New Comment, where the value is set once and
 * never changes.
 *
 * <p>Hence the clone. One render after mount the value is handed over again as
 * a NEW Date carrying the same instant, the library's effect runs with the
 * anchor present, and v11 formats the field with v11's own code -- no date
 * formatter is written here, which would be the v9 shape and would drift from
 * the locale the moment anyone set one. Neither of the alternatives is
 * acceptable: passing `dateFormat` to the Root does nothing (it is the binding
 * that is dead, not the value), and a `value` of our own on DatePicker.Input
 * does win the prop merge but only by reimplementing the format grammar.
 * <b>Delete all of this when the defect is fixed upstream</b> --
 * test/EsDatePicker.test.jsx asserts the field is filled, so it will not go
 * unnoticed.
 */
export function EsDatePicker( { id, name, value, onChange, autoComplete = 'off', 'data-testid': dataTestId } ) {
    const [ aMounted, setMounted ] = useState( false );

    useEffect( () => setMounted( true ), [] );

    // A clone, not a new date: the same instant under a new identity, which is
    // all v11's effect needs to notice. Only a Date is cloned -- null and
    // undefined are passed through untouched, so a record with no date keeps an
    // empty field rather than quietly becoming today.
    const aValue = useMemo(
        () => ( aMounted && value instanceof Date
            ? new Date( value.getTime() ) : value ),
        [ aMounted, value ] );

    return (
        <DatePicker.Root
            name={ name }
            value={ aValue }
            onValueChange={ ( event ) => onChange
                && onChange( { target: { name, value: event.value } } ) }
        >
            { /* The id goes on the Input, because that is what a test fills and
                 what it reads a date back out of; the calendar button is
                 <id>-trigger. The day cells are v11's own generated grid and
                 carry no hook of ours -- type the date instead. */ }
            <DatePicker.Input id={ id } name={ name } autoComplete={ autoComplete } data-testid={ dataTestId } />
            <DatePicker.Trigger type="button" data-testid={ dataTestId && `${ dataTestId }-trigger` }>
                <i className="pi pi-calendar" />
            </DatePicker.Trigger>
            <DatePicker.Portal>
                <DatePicker.Positioner>
                    <DatePicker.Popup>
                        <DatePicker.Panel>
                            <DatePicker.Header>
                                <DatePicker.Prev>
                                    <i className="pi pi-chevron-left" />
                                </DatePicker.Prev>
                                <DatePicker.Title>
                                    { /* Both render their own label: SelectMonth
                                         falls back to getMonthName() and
                                         SelectYear to getYear(). SelectMonth
                                         hides itself outside the date view and
                                         SelectYear inside the year view, which
                                         is why the header is unconditional. */ }
                                    <DatePicker.SelectMonth />
                                    <DatePicker.SelectYear />
                                </DatePicker.Title>
                                <DatePicker.Next>
                                    <i className="pi pi-chevron-right" />
                                </DatePicker.Next>
                            </DatePicker.Header>
                            <DatePicker.Body>
                                <DatePicker.Calendar>
                                    <DatePicker.Table>
                                        <DatePicker.TableHead />
                                        <DatePicker.TableBody view="date" />
                                        <DatePicker.TableBody view="month" />
                                        <DatePicker.TableBody view="year" />
                                    </DatePicker.Table>
                                </DatePicker.Calendar>
                            </DatePicker.Body>
                        </DatePicker.Panel>
                    </DatePicker.Popup>
                </DatePicker.Positioner>
            </DatePicker.Portal>
        </DatePicker.Root>
    );
}

// ---------------------------------------------------------------------------
// Inputs
//
// Added 2026-09-02 with characters. See the note at the top of the file: every
// one of these reshapes v11's own change event into { target: { name, value } }.
// ---------------------------------------------------------------------------

const clampNumber = ( pValue, pMin, pMax ) => {
    if ( pValue === null ) return null;
    if ( pMax != null && pValue > pMax ) return pMax;
    if ( pMin != null && pValue < pMin ) return pMin;
    return pValue;
};

/**
 * A number field with steppers, which v9 spelled the same way.
 *
 * Root carries the state and the formatting; Group, Decrement, Input and
 * Increment are the visible parts. The two buttons render EMPTY without a
 * child, exactly as Tree.Toggle and the Paginator's nav buttons do -- the parts
 * ship no icons -- so the chevrons are supplied here.
 *
 * `format` is off. The sheet's numbers are ability scores, hit points and coin
 * counts, and v11's default useGrouping would draw a starting purse of 1500 gp
 * as "1,500" and a hit point total of 1200 as "1,200". Neither is wrong to look
 * at and both are wrong to type into.
 *
 * <b>It fires once on mount, echoing the value it was given, and this is the
 * only component here that does</b> -- Select, Checkbox, Textarea and Stepper
 * were all probed and none of them do. Left alone that is a change event per
 * number field per page load, and a character sheet has about thirty: enough to
 * make an untouched sheet save itself the moment it opens, and worse than that,
 * enough for thirty handlers built on a stale render to overwrite each other.
 *
 * So an echo is dropped. A "change" to the value the field already holds is not
 * a change, which is true of the mount echo and of a person retyping the same
 * number, and neither is worth telling the caller about.
 *
 * <b>Every keystroke is reported, through Root's onChange, and the box holds a
 * draft of its own while it has focus.</b> v11's Input renders
 * value={ formattedValue } -- it is React-controlled -- while its keystroke
 * handlers write input.value and nothing else, and onValueChange waits for the
 * blur. So any render between two keystrokes put the held value back: a level
 * of 5, Backspace, the sheet re-rendering, then 9, read "59" and saved 20. The
 * draft is what the field is handed until the blur, so neither a parent's
 * re-render nor a parent that shows a default for empty (EsCodexNodeTags) can
 * write over the typing. An edit is clamped to min and max as v11's own blur
 * would clamp it, and empty is null.
 */
export function EsInputNumber( {
    id, name, value, onChange, min, max, step = 1, disabled, className,
    showButtons = true, fluid = true, readOnly = false, ariaLabel, autoComplete = 'off',
    'data-testid': dataTestId,
} ) {
    const aCurrent = value ?? null;
    // undefined: nobody is typing, and the field shows the caller's value. Held only
    // while the input has focus: a stepper press reports through onChange too, and
    // no blur would ever follow it to let the caller's value back in.
    const [ draft, setDraft ] = useState( undefined );
    const focused = useRef( false );

    const report = ( pNext ) => {
        if ( pNext === aCurrent ) return;
        if ( onChange ) onChange( { target: { name, value: pNext } } );
    };

    return (
        <InputNumber.Root
            className={ className }
            fluid={ fluid }
            value={ draft === undefined ? aCurrent : draft }
            min={ min }
            max={ max }
            step={ step }
            format={ false }
            disabled={ disabled }
            readOnly={ readOnly }
            onChange={ ( event ) => {
                const aNext = clampNumber( event.value ?? null, min, max );
                if ( focused.current ) setDraft( aNext );
                report( aNext );
            } }
            onValueChange={ ( event ) => {
                const aNext = event.value ?? null;
                if ( focused.current && draft !== undefined ) setDraft( aNext );
                report( aNext );
            } }
        >
            { /* The Input is a SIBLING of the Group, never a child of it.
                 v11's Group is not a wrapper around the control -- with the
                 stacked layout it is an absolutely positioned overlay sized to
                 sit on the field's right-hand edge (position: absolute,
                 inset-inline-end: 1px, height: calc(100% - 2px)). Put the
                 input inside it and the input is absolutely positioned too:
                 .p-inputnumber collapses to nothing, its grid cell collapses
                 with it, and the field escapes across the page. That is
                 exactly what every number on the character sheet did.

                 Increment before Decrement because stacked gives the first a
                 border-start-end-radius and the second a border-end-end-radius
                 -- plus on top, minus underneath.

                 No Group at all when there are no buttons. The stacked class
                 is applied from `hasGroup`, so an empty Group would still
                 declare a layout the field does not have. */ }
            { /* aria-label is spelled out as a prop rather than spread, for
                 the reason every wrapper in this file takes an explicit list:
                 an unlisted prop is dropped in silence. Grouped fields need it
                 -- three boxes under one visible "Hit points" have one label
                 between them otherwise. */ }
            { /* data-testid rides with aria-label and for the same reason. It
                 goes on the Input rather than the Root, because a test fills a
                 number field and reads its value back; the steppers are
                 <id>-increment and <id>-decrement. */ }
            <InputNumber.Input id={ id } name={ name } aria-label={ ariaLabel } autoComplete={ autoComplete }
                               data-testid={ dataTestId }
                               onFocus={ () => { focused.current = true; } }
                               onBlur={ () => {
                                   focused.current = false;
                                   setDraft( undefined );
                               } } />
            { showButtons && !readOnly && (
                <InputNumber.Group>
                    <InputNumber.Increment
                        data-testid={ dataTestId && `${ dataTestId }-increment` }>
                        <i className="pi pi-plus" />
                    </InputNumber.Increment>
                    <InputNumber.Decrement
                        data-testid={ dataTestId && `${ dataTestId }-decrement` }>
                        <i className="pi pi-minus" />
                    </InputNumber.Decrement>
                </InputNumber.Group>
            ) }
        </InputNumber.Root>
    );
}

/**
 * What v9 called Dropdown.
 *
 * Root takes options/optionLabel/optionValue and does the resolving, and
 * Select.List draws one Option per option all by itself when it is given no
 * children -- so the list here is deliberately empty. The rest is the trigger,
 * the portal and the popup, which are the parts that have to be assembled.
 *
 * The arrow is ours for the usual reason, and so is the empty message: v11's
 * Select.Empty renders whatever child it is given and nothing otherwise, which
 * on a step whose options have not loaded yet is an unexplained empty box.
 *
 * <b>There is no filter, on purpose.</b> v11 ships a Select.Filter part, it
 * renders a real <input>, and that input is wired to NOTHING: useSelect's
 * defaultProps declare no filterValue, no onFilterValueChange and no filter of
 * any kind, and the options go to useListbox unfiltered. The props exist in the
 * TYPES and are dropped before setup() reads them -- the identical mechanism to
 * Tree.Selection's inert `mode` (EsPrimeProbe2), which is now twice. Probed in
 * EsPrimeProbe4: the box accepts text and the list does not change. So the part
 * is not assembled here, because a search box that silently does nothing is
 * worse than no search box. If a list ever gets long enough to need one, the
 * filtering is ours to do over the `options` array.
 *
 * optionDisabled defaults to 'disabled' rather than to undefined, so an option
 * carrying { disabled: true } is greyed out and unselectable without the caller
 * having to name the field. The builder's standard-array pickers rely on it:
 * a score already spoken for by another ability stays VISIBLE -- otherwise the
 * six lists are each a different length and the spread cannot be seen -- and is
 * not pickable. Left unset, v11 resolves no disabled flag at all and every one
 * of those options is selectable, which renders identically and is wrong.
 *
 * <b>optionTemplate</b> draws each row itself: Select.List is then handed one
 * Select.Option per option, by index, so the listbox still resolves the
 * label, value, selection and disabled flag off `options` -- only the row's
 * content is ours. The closed box still prints the option's label.
 * <b>header</b> is a row above the list (Select.Header) and <b>popupClassName</b>
 * styles the popup, which is portalled out of the field's own markup.
 *
 * <b>fixed</b> draws the closed box with no arrow and keeps it shut: a label in
 * the same spot and style as the dropdown it is when there is a choice. The
 * changelog's release picker is one when a screenshot has a single version.
 * Not `disabled`, which grays the value out.
 *
 * <b>positionerClassName</b> classes the popup's positioner, which is where
 * PrimeReact writes its layer: z-index 1000 up, inline, and Select.Positioner
 * does not forward the positioner's baseZIndex. A select inside EsFullView
 * passes ES_FULL_VIEW_LAYER, or its list opens behind the view.
 *
 * <b>An option whose value is ''</b> -- No form, None, Untyped -- is a real
 * choice, and v11 cannot show it: useSelect's hasValue is isNotEmpty, so a box
 * holding '' prints the placeholder, or nothing. Such an option goes in as
 * BLANK_CHOSEN, a value of '' with it, and onChange turns it back into '', so
 * the caller never sees the stand-in. A null value is still "nothing chosen".
 *
 * <b>clearLabel</b> draws an X in the box while it holds a pick, a button of that name, and a
 * press is an onChange to null. v11's own Select.Clear, so the select does the clearing and the
 * X goes with the pick. Addressed as `<id>-clear`.
 */
export function EsSelect( {
    id, name, value, options = [], optionLabel = 'label', optionValue = 'value', fluid = true,
    optionDisabled = 'disabled', optionTemplate, header, popupClassName, fixed = false, positionerClassName,
    placeholder, onChange, disabled, className, emptyMessage, size, clearLabel,
    'aria-label': ariaLabel, 'data-testid': dataTestId,
} ) {
    const t = useEsStrings();
    const aIsBlank = ( pOption ) => pOption?.[ optionValue ] === '';
    const aHasBlank = Array.isArray( options ) && options.some( aIsBlank );
    const aOptions = aHasBlank
        ? options.map( ( pOption ) => ( aIsBlank( pOption )
            ? { ...pOption, [ optionValue ]: BLANK_CHOSEN } : pOption ) )
        : options;
    const aValue = aHasBlank && value === '' ? BLANK_CHOSEN : value ?? null;

    return (
        <Select.Root
            id={ id }
            data-testid={ dataTestId }
            fluid={ fluid }
            size={ size }
            name={ name }
            className={ className }
            value={ aValue }
            options={ aOptions }
            optionLabel={ optionLabel }
            optionValue={ optionValue }
            optionDisabled={ optionDisabled }
            disabled={ disabled }
            { ...( fixed ? { open: false } : {} ) }
            onValueChange={ ( event ) => onChange && onChange( { target: { name,
                value: event.value === BLANK_CHOSEN ? '' : event.value } } ) }
        >
            { /* The Root is a div wrapping the Trigger, so a click on the Root's
                 id lands on the button either way; <id>-trigger is there for a
                 step that wants the button itself, and <id>-value for reading
                 the current selection back without the arrow's text.

                 <b>The OPTIONS carry no id of ours.</b> Select.List generates
                 one Option per entry when it is given no children -- that is the
                 whole reason it is empty here -- and the only way to stamp them
                 would be to hand it children, which means reimplementing the
                 grouping and the label/value resolving it does for free. Address
                 an option by its text inside <id>-popup instead; that is what
                 aquifer's `user selects element … in element …` is for. */ }
            { /* The name goes on the TRIGGER, which is the button a reader
                 actually lands on -- the Root is a div and a label on it names
                 nothing focusable. Without it a filter dropdown is a button
                 whose whole accessible name is the value it happens to be
                 showing, which is "Any" until somebody picks something. */ }
            <Select.Trigger type="button" aria-label={ ariaLabel }
                            data-testid={ dataTestId && `${ dataTestId }-trigger` }>
                <Select.Value placeholder={ placeholder }
                              data-testid={ dataTestId && `${ dataTestId }-value` } />
                { clearLabel && !fixed && !disabled &&
                    <Select.Clear role="button" aria-label={ clearLabel }
                                  data-testid={ dataTestId && `${ dataTestId }-clear` }>
                        <i className="pi pi-times" aria-hidden="true" />
                    </Select.Clear> }
                { !fixed &&
                    <Select.Indicator>
                        <i className="pi pi-chevron-down" />
                    </Select.Indicator> }
            </Select.Trigger>
            <Select.Portal>
                <Select.Positioner className={ positionerClassName }>
                    <Select.Popup className={ popupClassName }
                                  data-testid={ dataTestId && `${ dataTestId }-popup` }>
                        { header && options.length > 0 && <Select.Header>{ header }</Select.Header> }
                        { optionTemplate
                            ? <Select.List>
                                { aOptions.map( ( pOption, pIndex ) => (
                                    <Select.Option key={ 'opt-' + String( pOption?.[ optionValue ] ?? pIndex ) }
                                                   index={ pIndex }>
                                        { optionTemplate( options[ pIndex ] ) }
                                    </Select.Option> ) ) }
                            </Select.List>
                            : <Select.List /> }
                        <Select.Empty data-testid={ dataTestId && `${ dataTestId }-empty` }>
                            { emptyMessage ?? t( 'common-select-empty', 'Nothing to choose from' ) }</Select.Empty>
                    </Select.Popup>
                </Select.Positioner>
            </Select.Portal>
        </Select.Root>
    );
}

// EsSelect's stand-in for an option valued ''. Never reaches a caller: onChange maps it back.
const BLANK_CHOSEN = '\u0000blank';

// The None option's value. Never reaches a caller: onChange strips it.
const NONE_CHOSEN = '\u0000none';

/**
 * What v9 called MultiSelect. v11 has no such component: it is Select.Root with
 * `multiple`, the value an array, and the popup left OPEN after each pick so
 * several can be ticked in one visit.
 *
 * The options are drawn here rather than left to Select.List, because a ticked
 * option needs an OptionIndicator inside it and generating children is the only
 * way to put one there. Each carries `<id>-option-<index>`; address one by its
 * text inside `<id>-popup` as with EsSelect.
 *
 * The box prints the picked labels joined by commas, in the order they were
 * picked, and when the box cannot show them all, as many as it shows whole and
 * the count of the rest: "Goblin Boss (+2)" (T26). `<id>-value` reads back what
 * shows; `<id>-labels` carries every label in `data-labels`, for a step that
 * asks what was picked rather than what fits.
 *
 * <b>noneLabel</b> draws one more option at the top that stands for the empty
 * list: ticked while nothing else is, and choosing it clears the rest. Choosing
 * anything else unticks it. The caller never sees its value, only [].
 */
export function EsMultiSelect( {
    id, name, value, options = [], fluid = true, noneLabel, placeholder, onChange,
    disabled, className, size, 'aria-label': ariaLabel, 'data-testid': dataTestId,
} ) {
    const aPicked = Array.isArray( value ) ? value : [];
    const aShown = noneLabel && aPicked.length === 0 ? [ NONE_CHOSEN ] : aPicked;
    const aOptions = noneLabel ? [ { label: noneLabel, value: NONE_CHOSEN }, ...options ] : options;
    const aLabels = aPicked.map( ( pValue ) => options.find( ( pOne ) => pOne.value === pValue )?.label )
        .filter( ( pLabel ) => pLabel !== undefined && pLabel !== null ).map( String );

    const change = ( pNext ) => {
        const aNext = Array.isArray( pNext ) ? pNext : [];
        const aNoneJustPicked = aNext.includes( NONE_CHOSEN ) && !aShown.includes( NONE_CHOSEN );

        const aValue = aNoneJustPicked ? [] : aNext.filter( ( pOne ) => pOne !== NONE_CHOSEN );
        if ( onChange ) onChange( { target: { name, value: aValue } } );
    };

    return (
        <Select.Root
            id={ id }
            data-testid={ dataTestId }
            multiple
            fluid={ fluid }
            size={ size }
            name={ name }
            className={ className }
            value={ aShown }
            options={ aOptions }
            optionLabel="label"
            optionValue="value"
            disabled={ disabled }
            onValueChange={ ( event ) => change( event.value ) }
        >
            <Select.Trigger type="button" aria-label={ ariaLabel }
                            data-testid={ dataTestId && `${ dataTestId }-trigger` }>
                <Select.Value placeholder={ placeholder }
                              data-testid={ dataTestId && `${ dataTestId }-value` }>
                    { aLabels.length > 0
                        ? <EsCountedLabels labels={ aLabels } data-testid={ dataTestId && `${ dataTestId }-labels` } />
                        : undefined }
                </Select.Value>
                <Select.Indicator>
                    <i className="pi pi-chevron-down" />
                </Select.Indicator>
            </Select.Trigger>
            <Select.Portal>
                <Select.Positioner>
                    <Select.Popup data-testid={ dataTestId && `${ dataTestId }-popup` }>
                        <Select.List>
                            { aOptions.map( ( pOne, pIndex ) => (
                                <Select.Option key={ pOne.value } index={ pIndex } uKey={ pOne.value }
                                               className="gap-2"
                                               data-testid={ dataTestId && `${ dataTestId }-option-${ pIndex }` }>
                                    <Select.OptionIndicator className="data-unselected:invisible">
                                        <i className="pi pi-check" />
                                    </Select.OptionIndicator>
                                    { pOne.label }
                                </Select.Option>
                            ) ) }
                        </Select.List>
                    </Select.Popup>
                </Select.Positioner>
            </Select.Portal>
        </Select.Root>
    );
}

/** The first pShown labels, and the count of the rest: "Goblin Boss, Wolf 1 (+2)". */
export const esCountedLabels = ( pLabels, pShown ) => ( pShown >= pLabels.length
    ? pLabels.join( ', ' ) : pLabels.slice( 0, pShown ).join( ', ' ) + ' (+' + ( pLabels.length - pShown ) + ')' );

/**
 * EsMultiSelect's box text, measured: the most labels that fit whole, then the count left over.
 * A probe, out of the flow, is filled with each candidate until one fits the box. A copy of the
 * whole list, invisible and no taller than nothing, holds the box's natural width at every label,
 * so a field sized to its text (the Cast tab, T5) does not narrow when the text is counted down.
 * With nothing to measure -- jsdom, a box not yet laid out -- every label shows.
 */
function EsCountedLabels( { labels, 'data-testid': dataTestId } ) {
    const box = useRef( null );
    const probe = useRef( null );
    const [ shown, setShown ] = useState( labels.length );
    const aKey = labels.join( '\u0000' );

    useLayoutEffect( () => {
        const aBox = box.current;
        const aProbe = probe.current;
        if ( !aBox || !aProbe ) return undefined;
        const measure = () => {
            const aWidth = aBox.clientWidth;
            let aFits = labels.length;
            if ( aWidth > 0 ) {
                while ( aFits > 1 ) {
                    aProbe.textContent = esCountedLabels( labels, aFits );
                    if ( aProbe.scrollWidth <= aWidth ) break;
                    aFits -= 1;
                }
            }
            setShown( aFits );
        };
        measure();
        if ( typeof ResizeObserver === 'undefined' ) return undefined;
        const aObserver = new ResizeObserver( measure );
        aObserver.observe( aBox );
        return () => aObserver.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ aKey ] );

    return (
        <span ref={ box } className="esCountedLabels" data-testid={ dataTestId } data-labels={ labels.join( ', ' ) }>
            <span className="esCountedLabelsText">{ esCountedLabels( labels, shown ) }</span>
            <span ref={ probe } className="esCountedLabelsProbe" aria-hidden="true" />
            <span className="esCountedLabelsWidth" aria-hidden="true">{ labels.join( ', ' ) }</span>
        </span>
    );
}

/**
 * A checkbox.
 *
 * v11 reports `checked`, not `value`, and calls the handler onCheckedChange.
 * Reshaped here like the others, so a sheet's handleChange treats a proficiency
 * toggle and an ability score identically.
 *
 * Box renders the frame and Indicator the mark inside it; the mark itself is
 * ours, on the same rule as every other icon in this file. It is drawn only
 * when checked, since an Indicator that always draws a tick is a checkbox that
 * is always on to look at.
 */
export function EsCheckbox( {
    id, name, checked, onChange, disabled, className, readOnly = false,
    'data-testid': dataTestId, 'data-tip': dataTip,
} ) {
    // On the Root, not the input: v11's own <input> is the visually hidden one
    // behind the box, and selenium cannot click an element it cannot see. The
    // Root is the box a person clicks. It carries data-checked while ticked (and
    // no aria-checked), which is what a test asserts.
    return (
        <Checkbox.Root
            className={ className }
            data-testid={ dataTestId }
            data-tip={ dataTip }
            readOnly={ readOnly }
            inputId={ id }
            name={ name }
            checked={ Boolean( checked ) }
            disabled={ disabled }
            onCheckedChange={ ( event ) => onChange
                && onChange( { target: { name, value: event.checked } } ) }
        >
            <Checkbox.Box>
                <Checkbox.Indicator>
                    { Boolean( checked ) && <i className="pi pi-check" /> }
                </Checkbox.Indicator>
            </Checkbox.Box>
        </Checkbox.Root>
    );
}

/**
 * A row of buttons of which exactly one is on -- a mode switch. v11's ToggleButtonGroup reports the pressed button's value
 * through onValueChange, and allowEmpty={ false } keeps the one that is on from
 * being pressed off (probed in test/EsPrimeProbe14.test.jsx).
 *
 * `options` is [ { value, label, icon } ]; each button's hook is `<id>-<value>`,
 * and aria-pressed says which is on.
 */
export function EsToggleGroup( { value, onChange, options = [], disabled, className,
    'data-testid': dataTestId } ) {
    return (
        <ToggleButtonGroup value={ value } allowEmpty={ false } multiple={ false }
                           disabled={ disabled } className={ className } data-testid={ dataTestId }
                           onValueChange={ ( event ) => {
                               if ( onChange && event.value != null ) onChange( event.value );
                           } }>
            { options.map( ( pOption ) => (
                <ToggleButton.Root key={ pOption.value } value={ pOption.value } type="button"
                                   data-testid={ dataTestId ? dataTestId + '-' + pOption.value : undefined }>
                    <ToggleButton.Indicator>
                        { pOption.icon && <i className={ 'pi ' + pOption.icon } aria-hidden="true" /> }
                        { pOption.label }
                    </ToggleButton.Indicator>
                </ToggleButton.Root>
            ) ) }
        </ToggleButtonGroup>
    );
}

/**
 * One button that is on or off -- a switch drawn as a button, for a bar of
 * controls where a checkbox would be the odd shape. v11's ToggleButton reports a
 * press through onPressedChange( { pressed } ), and aria-pressed says its state.
 */
export function EsToggle( { pressed, onChange, label, icon, disabled, className,
    'data-testid': dataTestId } ) {
    return (
        <ToggleButton.Root pressed={ Boolean( pressed ) } disabled={ disabled } type="button"
                           className={ className } data-testid={ dataTestId }
                           onPressedChange={ ( event ) => onChange && onChange( event.pressed ) }>
            <ToggleButton.Indicator>
                { icon && <i className={ 'pi ' + icon } aria-hidden="true" /> }
                { label }
            </ToggleButton.Indicator>
        </ToggleButton.Root>
    );
}

/**
 * A multi-line text field.
 *
 * The one component here that is NOT compositional: v11's Textarea is a single
 * element that renders a real <textarea> and passes native props straight
 * through, so this takes value/onChange the DOM way and needs no reshaping at
 * all. It is wrapped anyway so that a screen imports every input from one
 * place, and so that autoResize -- which is the whole reason to prefer it over
 * a bare <textarea> on a sheet full of prose panels -- is on by default.
 */
export function EsTextarea( {
    id, name, value, onChange, onKeyDown, onFocus, onBlur, rows = 3, placeholder, disabled, className,
    autoResize = true, fluid = true, readOnly = false, autoComplete = 'off', 'aria-label': ariaLabel, 'data-testid': dataTestId,
} ) {
    return (
        <Textarea
            id={ id }
            aria-label={ ariaLabel }
            autoComplete={ autoComplete }
            data-testid={ dataTestId }
            fluid={ fluid }
            name={ name }
            className={ className }
            value={ value ?? '' }
            rows={ rows }
            placeholder={ placeholder }
            disabled={ disabled }
            readOnly={ readOnly }
            autoResize={ autoResize }
            onChange={ onChange }
            onKeyDown={ onKeyDown }
            onFocus={ onFocus }
            onBlur={ onBlur }
        />
    );
}

/**
 * A step-by-step wizard.
 *
 * Takes `steps` as [ { value, title, content } ] rather than children, because
 * v11 wants each step declared twice -- once in the List as a header and once
 * in the Panels as a body -- and keeping the two in step by hand is exactly the
 * kind of duplication this file exists to remove.
 *
 * `value` is the active step and is a controlled prop, so the caller owns which
 * step is showing and can move it from its own Back/Next buttons. That matters
 * for a character builder: the SRD's five steps have to be walked in order and
 * a step is only leavable once its choice is made, which is a rule about the
 * character rather than about the stepper.
 *
 * The step numbers are ours to draw. Stepper.Number renders whatever child it
 * is given and nothing otherwise, the same as every other part here.
 */
export function EsStepper( { value, onChange, steps = [], linear = false, className,
    'data-testid': dataTestId } ) {
    // Generated from `steps`, so derived: <id>-step-<step value> for the header
    // a person clicks, <id>-panel-<step value> for the body it shows.
    const stepTestId = ( pPart, pStep ) => dataTestId
        && `${ dataTestId }-${ pPart }-${ pStep.value }`;

    return (
        <Stepper.Root
            className={ className }
            data-testid={ dataTestId }
            value={ value }
            linear={ linear }
            onValueChange={ ( event ) => onChange && onChange( event.value ) }
        >
            <Stepper.List>
                { steps.map( ( step, index ) => (
                    <Stepper.Item key={ step.value } value={ step.value }>
                        <Stepper.Step data-testid={ stepTestId( 'step', step ) }>
                            <Stepper.Header>
                                <Stepper.Number>{ index + 1 }</Stepper.Number>
                                <Stepper.Title>{ step.title }</Stepper.Title>
                            </Stepper.Header>
                        </Stepper.Step>
                        { index < steps.length - 1 && <Stepper.Separator /> }
                    </Stepper.Item>
                ) ) }
            </Stepper.List>
            <Stepper.Panels>
                { steps.map( ( step ) => (
                    <Stepper.Panel key={ step.value } value={ step.value }
                                   data-testid={ stepTestId( 'panel', step ) }>
                        <Stepper.Content>{ step.content }</Stepper.Content>
                    </Stepper.Panel>
                ) ) }
            </Stepper.Panels>
        </Stepper.Root>
    );
}

/**
 * A tab strip and its panels, driven from outside.
 *
 * <p><b>Controlled, always.</b> `value` and `onValueChange` are in
 * useTabs' own defaultProps -- checked in
 * node_modules/@primereact/headless/tabs, which declares
 * { value, defaultValue, onValueChange, selectOnFocus, scrollStrategy,
 * tabIndex } and nothing else. That check is the house rule earned twice over:
 * Tree.Selection's `mode` and Select.Filter's `filterValue` are both in the
 * TYPES, are both dropped before setup() reads them, and both render a control
 * that looks like it works. A prop this file passes is a prop verified to be in
 * a part's defaultProps.
 *
 * <p>Controlled matters here beyond taste: the character sheet draws the same
 * section list twice -- a rail at xl and this strip below it -- and two
 * widgets that each kept their own idea of the current section would disagree
 * the moment either was used.
 *
 * <p><b>Prev/Next are v11's own, repainted.</b> The strip's scrollbar is hidden
 * (.p-tablist-content), so without them a strip too wide for its panel said so
 * only by a word cut in half (Tim, 2026-09-24). They were left out once
 * because Lara paints .p-tablist-nav-button with {content.background} and a
 * `0 0 10px 50px` white box-shadow -- a white blob at each end on this app's
 * blue-900 -- so EsTheme.css paints them in the app's tokens instead. v11
 * renders each only while there is more that way, and scrolls four fifths of
 * the strip per press; useTabs' scrollStrategy "nearest" still brings a
 * selected tab into view.
 *
 * <p>Every panel stays mounted. Tabs.Panel's own default is lazy: false, and
 * the inactive ones are hidden with an inline style rather than unmounted --
 * which is what the character sheet needs, because a field edited and then left
 * behind by a tab switch must keep its pending value.
 *
 * <p>onTabClick fires on every press of a tab, the active one's included, where onChange
 * fires only when the value moves: a caller whose active tab is also a way to open something
 * needs the press. It listens on the Root and not on each Tab, because a Tab given an
 * onClick of its own loses v11's, and the tab stops selecting.
 */
export function EsTabs( { value, onChange, onTabClick, tabs = [], className, listClassName, end,
    'data-testid': dataTestId } ) {
    // Generated from `tabs`, so derived: <id>-tab-<tab value> for the button,
    // <id>-panel-<tab value> for its panel. Every panel stays mounted (see
    // below), so a panel's id resolves whether or not its tab is the active
    // one -- assert on what is DISPLAYED, not on what is present.
    const tabTestId = ( pPart, pTab ) => dataTestId && `${ dataTestId }-${ pPart }-${ pTab.value }`;

    return (
        <Tabs.Root
            className={ className }
            data-testid={ dataTestId }
            value={ value }
            onValueChange={ ( event ) => onChange && onChange( event.value ) }
            { ...( onTabClick && { onClick: ( pEvent ) => pEvent.target.closest( '[role="tab"]' ) && onTabClick() } ) }
        >
            <Tabs.List className={ listClassName }>
                { /* The strip is the scroller and its two arrows, wrapped so the
                     arrows sit at the ends of the TABS and not over `end`'s
                     buttons -- v11 positions them absolute at the nearest
                     positioned box, which was the whole List. See EsScrollX for
                     the fade that goes with them. */ }
                <div className="esTabStrip">
                    <Tabs.Prev ariaLabel="Scroll the tabs left"
                               data-testid={ dataTestId && `${ dataTestId }-scroll-prev` }>
                        <i className="pi pi-chevron-left" aria-hidden="true" />
                    </Tabs.Prev>
                    { /* Content is the scroller. Without it the tabs are children of
                         .p-tablist, which is overflow: hidden -- so a strip too wide
                         for a phone simply loses its last tabs. */ }
                    <Tabs.Content>
                        { tabs.map( ( pTab ) => (
                            <Tabs.Tab key={ pTab.value } value={ pTab.value }
                                      data-testid={ tabTestId( 'tab', pTab ) }>
                                { pTab.title }
                            </Tabs.Tab>
                        ) ) }
                    </Tabs.Content>
                    <Tabs.Next ariaLabel="Scroll the tabs right"
                               data-testid={ dataTestId && `${ dataTestId }-scroll-next` }>
                        <i className="pi pi-chevron-right" aria-hidden="true" />
                    </Tabs.Next>
                </div>
                { /* Beside the scroller rather than in it, so it stays put at
                     the strip's right edge: Content is flex-grow 1 inside a
                     flex List. */ }
                { end }
            </Tabs.List>
            <Tabs.Panels>
                { tabs.map( ( pTab ) => (
                    <Tabs.Panel key={ pTab.value } value={ pTab.value }
                                data-testid={ tabTestId( 'panel', pTab ) }>
                        { pTab.content }
                    </Tabs.Panel>
                ) ) }
            </Tabs.Panels>
        </Tabs.Root>
    );
}

/**
 * A modal dialog -- the app's first, which is why several comments elsewhere
 * say "this app has no EsDialog yet".
 *
 * <p>Controlled, like EsTabs and EsStepper: `open` and `onOpenChange` are both
 * in useDialog's defaultProps, so the caller owns whether it is showing and
 * this component owns none of it. That matters for a confirmation, where the
 * thing being confirmed lives in the caller's state anyway.
 *
 * <p>Portal and Backdrop are not optional decoration. Without the Portal the
 * popup renders inside whatever it was declared in and inherits that stacking
 * context, so a dialog opened from inside a card can end up behind the next
 * card; without the Backdrop there is nothing to darken the page or to catch
 * the click that dismisses it. Positioner is what centres it.
 *
 * <p>The close X is ours -- Dialog.Close renders its child and nothing on its
 * own, the same as every other v11 part that could have shipped an icon.
 * <b>headerActions</b> are controls drawn in the header beside it.
 */
export function EsDialog( {
    open, onOpenChange, title, footer, className, children, headerActions,
    dismissable = true, closeOnEscape = true, 'data-testid': dataTestId,
} ) {
    const t = useEsStrings();
    return (
        <Dialog.Root open={ open } onOpenChange={ ( event ) =>
            onOpenChange && onOpenChange( event.open ) }
                     modal dismissable={ dismissable } closeOnEscape={ closeOnEscape }>
            <Dialog.Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    { /* On the Popup, not the Root: Root is a context provider
                         and renders no element at all, and the Popup is what the
                         Portal puts in the body. The close X is <id>-close and
                         the heading <id>-title. */ }
                    <Dialog.Popup className={ className } data-testid={ dataTestId }>
                        <Dialog.Header>
                            <Dialog.Title data-testid={ dataTestId && `${ dataTestId }-title` }>
                                { title }</Dialog.Title>
                            <Dialog.HeaderActions>
                                { headerActions }
                                <Dialog.Close aria-label={ t( 'common-close', 'Close' ) }
                                              data-testid={ dataTestId && `${ dataTestId }-close` }>
                                    <i className="pi pi-times" />
                                </Dialog.Close>
                            </Dialog.HeaderActions>
                        </Dialog.Header>
                        { /* display: contents, so the wrapper adds no box: it is the root the blank
                             field is looked for under (EsFocusBlank). */ }
                        <Dialog.Content><div className="contents" ref={ esFocusBlankOnMount }>{ children }</div></Dialog.Content>
                        { footer && <Dialog.Footer>{ footer }</Dialog.Footer> }
                    </Dialog.Popup>
                </Dialog.Positioner>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

/**
 * Whether a sideways scroller has more to either side of what shows.
 *
 * <p>Measured on scroll and on resize of the box or of what is in it -- a row
 * joining the fight widens the table without the box changing at all. The
 * same test useTabs makes for its own strip, so the two cues agree.
 *
 * @return { prev, next }
 */
export function useEsScrollEdges( ref ) {
    const [ edges, setEdges ] = useState( { prev: false, next: false } );

    useEffect( () => {
        const aBox = ref.current;
        if ( !aBox ) return undefined;

        const measure = () => {
            const aLeft = Math.abs( aBox.scrollLeft );
            const aPrev = aLeft > 1;
            const aNext = aBox.scrollWidth - aBox.clientWidth - aLeft > 1;
            setEdges( ( pWas ) => ( pWas.prev === aPrev && pWas.next === aNext
                ? pWas : { prev: aPrev, next: aNext } ) );
        };

        measure();
        aBox.addEventListener( 'scroll', measure, { passive: true } );
        const aObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver( measure );
        aObserver?.observe( aBox );
        if ( aBox.firstElementChild ) aObserver?.observe( aBox.firstElementChild );

        return () => {
            aBox.removeEventListener( 'scroll', measure );
            aObserver?.disconnect();
        };
    }, [ ref ] );

    return edges;
}

/**
 * A sideways scroller that SAYS it scrolls: a fade on each edge with more
 * beyond it, and an arrow there that moves four fifths of a view (Tim,
 * 2026-09-24: "it's up to chance to show that a word is half showing"). A Mac
 * hides the scrollbar until a swipe, so without this the only sign was a
 * column cut through the middle.
 *
 * <p>Nothing shows while everything fits, so at full width the Roster looks as
 * it did. The fade and arrows are EsTheme.css's, shared with EsTabs' strip.
 *
 * @param className the scroller's own classes -- it is the inner box
 */
export function EsScrollX( { className, children, tabIndex, 'aria-label': ariaLabel, 'data-testid': dataTestId } ) {
    const t = useEsStrings();
    const boxRef = useRef( null );
    const { prev, next } = useEsScrollEdges( boxRef );

    const scroll = ( pSign ) => {
        const aBox = boxRef.current;
        if ( !aBox ) return;
        // Prev is toward the start of the line, which is the right in a right-to-left page.
        const aRtl = getComputedStyle( aBox ).direction === 'rtl';
        const aBy = ( aRtl ? -pSign : pSign ) * aBox.clientWidth * 0.8;
        if ( aBox.scrollBy ) aBox.scrollBy( { left: aBy, behavior: 'smooth' } );
        else aBox.scrollLeft += aBy;
    };

    return (
        <div className="esScrollX"
             { ...( prev ? { 'data-can-scroll-prev': '' } : {} ) }
             { ...( next ? { 'data-can-scroll-next': '' } : {} ) }>
            { /* tabIndex and a label for a box with nothing focusable inside, so a
                 keyboard can scroll it (axe's scrollable-region-focusable). */ }
            <div ref={ boxRef } className={ className } data-testid={ dataTestId }
                 tabIndex={ tabIndex } aria-label={ ariaLabel }
                 role={ tabIndex === undefined ? undefined : 'region' }>
                { children }
            </div>
            { prev &&
                <button type="button" className="esScrollXButton esScrollXPrev"
                        data-testid={ dataTestId && `${ dataTestId }-scroll-prev` }
                        aria-label={ t( 'common-scroll-left', 'Scroll left' ) } onClick={ () => scroll( -1 ) }>
                    <i className="pi pi-chevron-left" aria-hidden="true" />
                </button> }
            { next &&
                <button type="button" className="esScrollXButton esScrollXNext"
                        data-testid={ dataTestId && `${ dataTestId }-scroll-next` }
                        aria-label={ t( 'common-scroll-right', 'Scroll right' ) } onClick={ () => scroll( 1 ) }>
                    <i className="pi pi-chevron-right" aria-hidden="true" />
                </button> }
        </div>
    );
}

/** The stand-ins esAnchorAt has laid, by purpose. */
const standIns = new Map();

/**
 * A stand-in anchor: an empty fixed box over a rectangle of the viewport, for
 * v11's positioner to place a popup against.
 *
 * <p><b>Why not the element itself.</b> v11 positions with CSS anchor
 * positioning where the browser has it, and an anchor inside a scrolled box --
 * the Roster, scrolled sideways in a console narrowed by the map -- places the
 * popup as though the box were not scrolled: a scroll's width away from what
 * was clicked (Tim, 2026-09-24). A fixed box in body is inside no scroller, so
 * its rectangle is the one the reader sees. Rectangles from clientX/Y and
 * getBoundingClientRect are already after every scroll.
 *
 * <p>Laid when the popup is asked for and not moved after. One per key: the
 * last one for the same purpose is taken away, so the page holds at most one
 * each.
 *
 * @param key  what it is for, e.g. "roster-menu"
 * @param rect { left, top, width, height } in viewport pixels
 * @return the element, for a Positioner's anchor
 */
export function esAnchorAt( key, rect ) {
    standIns.get( key )?.remove();
    const aBox = document.createElement( 'span' );
    aBox.setAttribute( 'aria-hidden', 'true' );
    aBox.dataset.esAnchor = key;
    Object.assign( aBox.style, {
        position: 'fixed', pointerEvents: 'none',
        left: rect.left + 'px', top: rect.top + 'px',
        width: ( rect.width ?? 0 ) + 'px', height: ( rect.height ?? 0 ) + 'px',
    } );
    // FIRST in body, not last: CSS anchor positioning only accepts an anchor
    // laid out before the popup, and the popups' portals are already there.
    document.body.prepend( aBox );
    standIns.set( key, aBox );
    return aBox;
}

/**
 * Where a click asked for its popup: the pointer, or -- a click from the
 * keyboard, which has no position (detail 0) -- the control it landed on.
 */
export function esClickRect( event ) {
    if ( event.detail > 0 ) return { left: event.clientX, top: event.clientY, width: 0, height: 0 };
    const aOn = event.target.closest?.( 'button, a, td, th' ) ?? event.target;
    return aOn.getBoundingClientRect();
}

/** How long a pointer rests on something before its tip shows. Short, because
 *  a tip is asked for by resting there; moving on from one showing tip to the
 *  next is immediate. */
const TIP_DELAY = 300;

/**
 * The house tooltip: one for the whole app, shown for whatever carries
 * `data-tip`.
 *
 * <p><b>Why not title.</b> Every hover label here was a native title, and the
 * operating system draws those -- in its own small type, after its own long
 * wait, and nothing in a stylesheet reaches either (Tim, 2026-09-24: "hover
 * label text should be bigger so you can read it"). So a hover label is
 * `data-tip="..."` on the element it describes, and this draws it.
 *
 * <p><b>Why one, delegated, rather than a Tooltip.Root around each.</b> v11's
 * Trigger is an element of its own -- a button by default -- and most of what
 * carries a label here is already a button, a link, a th or a td. Wrapping
 * each would nest buttons and put a span inside table rows. The Root takes
 * an `anchor` instead, and Positioner places against it, so a single Root
 * mounted once (index.js) follows the pointer: the attribute stays where the
 * title was, on the element it was always on.
 *
 * <p>Open is driven from here rather than by v11's own listeners, which it
 * attaches to the anchor when the anchor is set -- after the pointer is
 * already inside it, so its pointerenter never comes. Its close events (the
 * pointer leaving, Escape) are honoured through onOpenChange.
 *
 * <p>A tip is a description, not a name: an element whose only label was its
 * title carries an aria-label as well.
 *
 * <p><b>A showing tip watches its element.</b> It is placed against a snapshot of the
 * element's box, and a still pointer sends no event, so a Roster row re-sorted, removed, or
 * scrolled under it left the tip behind over another row (Tim, 2026-10-22: "cinder" over
 * Goblin Boss). Any page change that moves, removes, or retitles the element, and any scroll,
 * takes the tip away; the next pointer move draws the right one.
 */
export function EsTipHost() {
    const [ tip, setTip ] = useState( null );
    const [ open, setOpen ] = useState( false );
    const live = useRef( { timer: null, current: null, showing: false, watch: null } );

    const hide = useCallback( () => {
        const aLive = live.current;
        clearTimeout( aLive.timer );
        aLive.timer = null;
        aLive.current = null;
        aLive.watch?.disconnect();
        aLive.watch = null;
        if ( aLive.showing ) {
            aLive.showing = false;
            setOpen( false );
        }
    }, [] );

    useEffect( () => {
        const aLive = live.current;
        const tipOf = ( pNode ) => pNode?.closest?.( '[data-tip]' ) ?? null;

        const show = ( pElement, pWait ) => {
            clearTimeout( aLive.timer );
            aLive.current = pElement;
            const aShow = () => {
                aLive.timer = null;
                const aText = pElement.getAttribute( 'data-tip' );
                if ( !aText || !pElement.isConnected ) return;
                aLive.showing = true;
                // A stand-in over the element rather than the element: a th in
                // the scrolled Roster would place its tip a scroll away.
                const aBox = pElement.getBoundingClientRect();
                setTip( { anchor: esAnchorAt( 'tip', aBox ), text: aText } );
                setOpen( true );
                aLive.watch?.disconnect();
                aLive.watch = new MutationObserver( () => {
                    const aNow = pElement.isConnected ? pElement.getBoundingClientRect() : null;
                    if ( !aNow || pElement.getAttribute( 'data-tip' ) !== aText
                        || aNow.left !== aBox.left || aNow.top !== aBox.top ) hide();
                } );
                aLive.watch.observe( document.body, { childList: true, subtree: true, attributes: true,
                    attributeFilter: [ 'data-tip' ] } );
            };
            if ( pWait > 0 ) aLive.timer = setTimeout( aShow, pWait );
            else aShow();
        };

        const onOver = ( pEvent ) => {
            const aElement = tipOf( pEvent.target );
            if ( aElement === aLive.current ) return;
            if ( !aElement ) hide();
            else show( aElement, aLive.showing ? 0 : TIP_DELAY );
        };
        // Out of the window altogether; anything else is the next pointerover.
        const onOut = ( pEvent ) => { if ( !pEvent.relatedTarget ) hide(); };
        // The keyboard's hover. Only a visible focus: a click focuses a button
        // too, and a click is an act, not a question.
        const onFocusIn = ( pEvent ) => {
            const aElement = tipOf( pEvent.target );
            if ( aElement && pEvent.target.matches?.( ':focus-visible' ) ) show( aElement, 0 );
        };
        const onFocusOut = ( pEvent ) => {
            if ( aLive.current && tipOf( pEvent.relatedTarget ) !== aLive.current ) hide();
        };
        const onKey = ( pEvent ) => { if ( pEvent.key === 'Escape' ) hide(); };
        const onScroll = () => { if ( aLive.showing ) hide(); };

        document.addEventListener( 'pointerover', onOver );
        document.addEventListener( 'pointerout', onOut );
        document.addEventListener( 'pointerdown', hide );
        document.addEventListener( 'focusin', onFocusIn );
        document.addEventListener( 'focusout', onFocusOut );
        document.addEventListener( 'keydown', onKey );
        document.addEventListener( 'scroll', onScroll, { capture: true, passive: true } );
        return () => {
            clearTimeout( aLive.timer );
            aLive.watch?.disconnect();
            document.removeEventListener( 'scroll', onScroll, { capture: true } );
            document.removeEventListener( 'pointerover', onOver );
            document.removeEventListener( 'pointerout', onOut );
            document.removeEventListener( 'pointerdown', hide );
            document.removeEventListener( 'focusin', onFocusIn );
            document.removeEventListener( 'focusout', onFocusOut );
            document.removeEventListener( 'keydown', onKey );
        };
    }, [ hide ] );

    return (
        <Tooltip.Root open={ open } anchor={ tip?.anchor ?? null }
                      onOpenChange={ ( event ) => {
                          if ( !( event.value ?? event.open ) ) hide();
                      } }>
            <Tooltip.Portal>
                <Tooltip.Positioner sideOffset={ 6 }>
                    <Tooltip.Popup className="esTip" data-testid="es-tip">
                        { tip?.text }
                    </Tooltip.Popup>
                </Tooltip.Positioner>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
}

/**
 * An overflow menu: one trigger, a list of things to do to one row.
 *
 * <p><b>Why a row owns a menu at all.</b> Every per-character action on the
 * game console used to begin by picking that character out of a dropdown in a
 * panel somewhere else on the page -- so a GM who could SEE the row they meant
 * had to name it again to a form. The menu puts the actions where the thing
 * they act on already is, which is the same argument the character sheet makes
 * by editing a number in the box that shows it.
 *
 * <p><b>The parts, read out of @primereact/ui/menu rather than guessed</b>
 * (they ship types and no prose): Root, Trigger, Portal, Positioner, Popup,
 * List, Item, Separator. Root reports opening through onOpenChange and an Item
 * reports a press through onSelect -- NOT onClick, which is the v9 shape and
 * renders perfectly while doing nothing.
 *
 * <p>Hooks derive from `data-testid` the way every wrapper in this file does:
 * `<id>-trigger`, `<id>-popup`, and `<id>-item-<key>` per item. An item's key
 * is the caller's, so a feature file can press one by name.
 *
 * @param label       what the trigger announces to a screen reader; there is no
 *                    visible text on it
 * @param items       { key, label, onSelect, danger, separatorBefore, testId },
 *                    where testId replaces the derived `<id>-item-<key>`
 * @param disabled    whether the whole menu is unavailable
 * @param icon        the trigger's PrimeIcon, pi-ellipsis-h unless named
 * @param triggerTestId replaces the derived `<id>-trigger`, for a caller whose
 *                    hooks were fixed before it had a menu
 * @param tip         the trigger's data-tip, if any
 */
export function EsMenu( { label, items = [], disabled, className, open, onOpenChange,
    anchor, icon = 'pi-ellipsis-h', triggerTestId, tip, 'data-testid': dataTestId } ) {
    // The icon, so EsMenuAnchor can find its way back to the Trigger. A ref on
    // the Trigger itself would be merged with v11's own anchor ref into a new
    // function every render, and React calling that would undo the anchor.
    const iconRef = useRef( null );
    return (
        // Controlled ONLY when the caller passes open. Left alone, v11 keeps its
        // own state and the trigger is the whole story; the game console passes
        // it because a click anywhere on a table row opens that row's menu, and
        // a row is not a thing the Trigger can be wrapped around.
        <Menu.Root { ...( open === undefined ? {} : { open } ) }
                   onOpenChange={ ( event ) =>
                       onOpenChange && onOpenChange( event.value ?? event.open ) }>
            <Menu.Trigger type="button" className={ className } disabled={ disabled }
                          aria-label={ label } data-tip={ tip }
                          data-testid={ triggerTestId ?? ( dataTestId && `${ dataTestId }-trigger` ) }>
                { /* Ours, on the rule every icon in this file follows: the v11
                     parts ship no icons at all. */ }
                <i ref={ iconRef } className={ 'pi ' + icon } aria-hidden="true" />
            </Menu.Trigger>
            { anchor !== undefined &&
                <EsMenuAnchor anchor={ anchor } open={ open } iconRef={ iconRef } /> }
            <Menu.Portal>
                <Menu.Positioner>
                    <Menu.Popup data-testid={ dataTestId && `${ dataTestId }-popup` }>
                        <Menu.List>
                            { items.filter( Boolean ).map( ( pItem ) => (
                                <React.Fragment key={ pItem.key }>
                                    { pItem.separatorBefore && <Menu.Separator /> }
                                    <Menu.Item
                                        className={ pItem.danger ? 'esMenuDanger' : undefined }
                                        data-testid={ pItem.testId ?? ( dataTestId
                                            && `${ dataTestId }-item-${ pItem.key }` ) }
                                        disabled={ pItem.disabled }
                                        onSelect={ pItem.onSelect }>
                                        { pItem.label }
                                    </Menu.Item>
                                </React.Fragment>
                            ) ) }
                        </Menu.List>
                    </Menu.Popup>
                </Menu.Positioner>
            </Menu.Portal>
        </Menu.Root>
    );
}

/**
 * Points an EsMenu's popup at an element other than its Trigger.
 *
 * <p><b>Why not Menu.Positioner's anchor prop.</b> It is typed, it is accepted,
 * and it is thrown away: MenuPositioner hands usePositioner
 * `popover.state.anchorElement` and nothing of its own. That element is set by
 * the Trigger's ref, so every menu anchored to its Trigger whatever it was
 * told -- which on the game console, with the roster scrolled sideways in a
 * narrow card, put a row's menu at the dots off past the card's right edge
 * instead of beside the cell that was clicked.
 *
 * <p>So this moves the popover's own anchor, through the setAnchorRef v11
 * exposes on the menu's context, and moves it back over the Trigger when the
 * caller's anchor is null -- the menu opened from the dots. Only while open:
 * a change on the way closed would jump the popup during its exit.
 *
 * @param anchor the element to open beside, or null for the Trigger
 * @param iconRef the Trigger's icon, the way back to the Trigger
 */
function EsMenuAnchor( { anchor, open, iconRef } ) {
    const setAnchorRef = useMenuContext()?.popover?.setAnchorRef;
    useLayoutEffect( () => {
        if ( !open || !setAnchorRef ) return;
        // The Trigger by way of a stand-in over it, for the reason esAnchorAt
        // gives: a Trigger in a scrolled box is placed a scroll away too.
        const aTrigger = iconRef.current?.closest( 'button' );
        const aTarget = anchor
            ?? ( aTrigger && esAnchorAt( 'menu-trigger', aTrigger.getBoundingClientRect() ) );
        if ( aTarget ) setAnchorRef( aTarget );
    }, [ anchor, open, setAnchorRef, iconRef ] );
    return null;
}

/**
 * A popover anchored to the thing it changes.
 *
 * <p>The middle tier between a menu item and a panel: for an action with a rule
 * behind it and two or three fields, on ONE value the caller can already see.
 * Damage on the hit points cell rather than a Hit points tab with a "Who"
 * picker in it.
 *
 * <p>Controlled, unlike EsMenu: the caller decides when it is open, because
 * closing it is something the caller's own save has to do.
 *
 * <p>Parts as shipped: Root, Trigger, Portal, Positioner, Popup, Content,
 * Header, Title, Footer. Hooks derive `<id>-trigger`, `<id>-popup` and
 * `<id>-title`.
 *
 * @param trigger what opens it -- rendered inside the Trigger, so it is the
 *                cell's own content and not a second control beside it
 */
export function EsPopover( { open, onOpenChange, title, trigger, footer, children,
    className, 'data-testid': dataTestId } ) {
    return (
        <Popover.Root open={ open } onOpenChange={ ( event ) =>
            onOpenChange && onOpenChange( event.value ?? event.open ) }>
            <Popover.Trigger type="button" className={ className }
                             data-testid={ dataTestId && `${ dataTestId }-trigger` }>
                { trigger }
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Positioner>
                    <Popover.Popup data-testid={ dataTestId && `${ dataTestId }-popup` }>
                        { title &&
                            <Popover.Header>
                                <Popover.Title
                                    data-testid={ dataTestId && `${ dataTestId }-title` }>
                                    { title }
                                </Popover.Title>
                            </Popover.Header> }
                        <Popover.Content><div className="contents" ref={ esFocusBlankOnMount }>{ children }</div></Popover.Content>
                        { footer && <Popover.Footer>{ footer }</Popover.Footer> }
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}

/**
 * A box you type into that offers what it finds.
 *
 * <p><b>This is what EsSelect cannot be.</b> v11 ships a Select.Filter part, it
 * renders a real input, and it is wired to NOTHING -- see EsSelect's javadoc and
 * EsPrimeProbe4, where the box accepts text and the list does not change. So a
 * field that has to search a thousand codex entries is an AutoComplete, which
 * is a different component rather than a Select with a switch thrown.
 *
 * <p><b>The caller does the searching.</b> onComplete hands over what has been
 * typed and the caller sets `options`; nothing here filters, because what this
 * is for is asking the SERVER -- a thousand entries is not a list to ship to a
 * browser so it can grep it. Debouncing is the caller's too, for the same
 * reason: only the caller knows what its endpoint costs.
 *
 * <p>Parts as shipped: Root, Input, Trigger, Portal, Positioner, Popup, List,
 * Option, Empty. The Option list generates itself from `options` when List is
 * given no children -- the same arrangement EsSelect relies on -- so an option
 * carries no hook of its own and is addressed by its text inside
 * `<id>-popup`.
 *
 * <p><b>`data-testid` lands on the ROOT, which is a div, and the box you type
 * into is `<id>-input`.</b> Worth saying because it is the difference between
 * this and every InputText it replaces: a step that kept filling the old id
 * answers InvalidElementStateException from Selenium, which is at least loud.
 *
 * <p><b>`trigger` draws a chevron that opens the list without typing</b>, for a
 * short list a reader should be able to see whole. v11's Trigger only toggles
 * the popup -- it fires no onComplete -- so what it shows is `options` as they
 * stand, and a caller that wants the whole list on a click keeps `options`
 * whole while the box is empty. Addressed as `<id>-trigger`.
 *
 * @param onSearch what has been typed, as it is typed
 * @param onPick   an option was chosen; handed the option, not an event
 * @param options  what to offer right now
 * @param trigger  draw the chevron that opens the list on a click
 *
 * <p><b>optionTemplate, header, optionDisabled</b> are EsSelect's, for the same
 * reasons: a row drawn as columns, a row of captions above them, and an option
 * greyed out and unpickable by a field of its own (default 'disabled').
 *
 * <p><b>optionGroupChildren</b> turns `options` into groups, each holding its
 * options under that field and drawn by optionGroupTemplate as a heading. v11's
 * listbox flattens them and steps over the headings with the arrow keys; a
 * heading is data-part="optionGroup", never an option.
 *
 * <p><b>Enter picks only an option the reader highlighted</b>, with an arrow key
 * or the pointer. v11 parks its highlight on the first option whenever the popup
 * closes, so the next Enter took that option over whatever had been typed -- a
 * typed AC 15 became the first creature (B6). Otherwise Enter is the form's, as
 * in any box.
 *
 * <p><b>onClear with clearLabel draws an X</b> in the box while it holds text,
 * a button named clearLabel. v11's own Clear part draws only while it holds a
 * pick, and this one never does (value below). Addressed as `<id>-clear`.
 *
 * <p><b>The chevron hands the box the focus</b> (B10). v11's Trigger only toggles the popup,
 * and a button that keeps the focus sends Up and Down to the page, which scrolled, while the
 * highlight never moved. <b>Up and Down open a chevron's list</b> from an empty box (T29), as
 * they open a Select's: v11 opens on an arrow only once something is typed. `open` is held
 * here for that alone; v11 still opens and closes it everywhere else, through onOpenChange.
 *
 * <p><b>fit</b> sizes the box to its text, or to its placeholder while empty (T5): an input's
 * own width is its `size`, never its value, so an invisible copy of the text shares the input's
 * grid cell and sets the width for both.
 */
export function EsAutoComplete( { id, name, value, options = [], optionLabel = 'label',
    placeholder, emptyMessage, disabled, className, fluid, trigger, popupClassName,
    optionTemplate, header, optionDisabled = 'disabled',
    optionGroupChildren, optionGroupTemplate, fit = false,
    onSearch, onPick, onClear, clearLabel, autoComplete = 'off', 'data-testid': dataTestId } ) {
    const t = useEsStrings();
    const highlighted = useRef( false );
    const [ open, setOpen ] = useState( false );
    // Capture, so it runs before v11's own key handler on the same box and can keep Enter from it.
    // Only while the list is open: closed, v11 picks nothing on Enter, and the key goes on as ever.
    const guardEnter = ( pEvent ) => {
        const aOpen = pEvent.currentTarget.getAttribute( 'aria-expanded' ) === 'true';
        if ( pEvent.key === 'Enter' ) {
            if ( !highlighted.current && aOpen ) {
                pEvent.stopPropagation();
            }
        }
        else if ( [ 'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown' ].includes( pEvent.key ) ) {
            highlighted.current = true;
            // v11's handler still runs after this and moves the highlight, so the one press opens
            // the list and lands on its first (or last) option.
            if ( trigger && !aOpen && options.length > 0 && ( pEvent.key === 'ArrowDown' || pEvent.key === 'ArrowUp' ) ) {
                setOpen( true );
            }
        }
        else if ( pEvent.key === 'Escape' ) {
            highlighted.current = false;
        }
    };
    return (
        <AutoComplete.Root
            id={ id }
            name={ name }
            className={ [ className, fit && 'esAutoCompleteFit' ].filter( Boolean ).join( ' ' ) || undefined }
            data-testid={ dataTestId }
            options={ options }
            optionLabel={ optionLabel }
            optionDisabled={ optionDisabled }
            // The group's own label is never shown -- optionGroupTemplate draws the
            // heading -- but v11 takes groups only when both fields are named.
            optionGroupLabel={ optionGroupChildren ? optionLabel : undefined }
            optionGroupChildren={ optionGroupChildren }
            inputValue={ value ?? '' }
            open={ open }
            onOpenChange={ ( event ) => setOpen( Boolean( event?.value ) ) }
            // Held at nothing, because the CALLER owns the pick. Left to itself
            // v11 remembers the last option chosen, and its listbox treats a
            // click on a selected option as a DESELECT -- so after the caller
            // cleared the box, picking the same option again (a second attack
            // on the same wolf) emptied it instead.
            value={ null }
            disabled={ disabled }
            fluid={ fluid }
            // NEVER hand the caller undefined. v11 reports these two events with
            // different fields and either can be absent -- onComplete carries a
            // query, onInputValueChange a value -- and a caller that put the
            // undefined into state and called .trim() on it took the whole
            // application down to the error page. That is not the caller's bug
            // to guard: a search box reports what is in it, and what is in an
            // empty box is an empty string.
            onInputValueChange={ ( event ) => {
                highlighted.current = false;
                onSearch && onSearch( event?.value ?? event?.query ?? '' );
            } }
            onComplete={ ( event ) =>
                onSearch && onSearch( event?.query ?? event?.value ?? '' ) }
            onValueChange={ ( event ) => {
                highlighted.current = false;
                onPick && onPick( event.value );
            } }
        >
            { /* p-inputtext because v11's Input carries p-autocomplete-input
                 ALONE, and the field look -- border, padding, fill, focus ring
                 -- is all written against p-inputtext, here and in EsTheme.css.
                 Without it the box drew as bare placeholder text beside a row
                 of InputTexts until it was focused. */ }
            <AutoComplete.Input className="p-inputtext" placeholder={ placeholder } autoComplete={ autoComplete }
                                size={ fit ? 1 : undefined }
                                onKeyDownCapture={ guardEnter }
                                data-testid={ dataTestId && `${ dataTestId }-input` } />
            { fit && <span className="p-inputtext esAutoCompleteSizer" aria-hidden="true">{ value || placeholder || '' }</span> }

            { trigger &&
                <AutoComplete.Trigger className="esAutoCompleteTrigger" type="button"
                                      onClick={ ( pEvent ) => {
                                          highlighted.current = false;
                                          pEvent.currentTarget.parentElement?.querySelector( 'input' )?.focus();
                                      } }
                                      data-testid={ dataTestId && `${ dataTestId }-trigger` }>
                    <i className="pi pi-chevron-down" />
                </AutoComplete.Trigger> }

            { /* type=button, or a press would submit the form the box sits in. Focus goes back
                 to the box, so the next keystroke types the number the X made room for. */ }
            { onClear && clearLabel && ( value ?? '' ) !== '' && !disabled &&
                <button type="button" className="esAutoCompleteClear" aria-label={ clearLabel }
                        data-testid={ dataTestId && `${ dataTestId }-clear` }
                        onClick={ ( pEvent ) => {
                            highlighted.current = false;
                            onClear();
                            pEvent.currentTarget.parentElement?.querySelector( 'input' )?.focus();
                        } }>
                    <i className="pi pi-times" aria-hidden="true" />
                </button> }

            { /* NO POPUP WHEN THERE IS NOTHING TO PUT IN IT, and this is not a
                 nicety -- it is the difference between a working form and a
                 blocked one.

                 The popup is PORTALLED and overlays the form, and it opened on
                 every keystroke: with no matches it still drew, carrying an
                 "Nothing found" message, and the fields around it became
                 present-but-unclickable. Selenium reports that as "gave up
                 waiting for it to be visible", which reads like a missing
                 element and is not; a person filling the form hits the same wall
                 and just sees a box in the way.

                 So the whole subtree is conditional. The cost is that
                 emptyMessage is only ever seen when a search has matches and
                 then loses them, which is the one case it is any use in. */ }
            { options.length > 0 &&
                <AutoComplete.Portal>
                    <AutoComplete.Positioner>
                        <AutoComplete.Popup className={ popupClassName }
                                            onPointerMove={ () => { highlighted.current = true; } }
                                            data-testid={ dataTestId && `${ dataTestId }-popup` }>
                            { header && <AutoComplete.Header>{ header }</AutoComplete.Header> }
                            { /* The options are drawn HERE rather than by an
                                 empty List, because the List keys each one
                                 `opt-${ value }`, and with no optionValue the
                                 value is the option itself: every object option
                                 -- a codex entry, a { label } -- keyed
                                 "opt-[object Object]", and React warned that the
                                 keys collide. Position plus label is unique by
                                 construction and asks no field of the caller;
                                 the list is replaced wholesale on every search,
                                 so a positional key costs nothing. */ }
                            <AutoComplete.List>
                                { ( pList ) => pList.options?.map( ( pOption, pIndex ) => {
                                    if ( pList.listbox?.isOptionGroup( pOption ) ) {
                                        return (
                                            <AutoComplete.Option key={ `group-${ pIndex }` } index={ pIndex } group>
                                                { optionGroupTemplate ? optionGroupTemplate( pOption.optionGroup )
                                                    : pList.listbox.getOptionGroupLabel( pOption.optionGroup ) }
                                            </AutoComplete.Option> );
                                    }
                                    const aLabel = pList.listbox?.getOptionLabel( pOption );
                                    return (
                                        <AutoComplete.Option key={ `opt-${ pIndex }-${ String( aLabel ) }` }
                                                             index={ pIndex }>
                                            { optionTemplate ? optionTemplate( pOption ) : aLabel }
                                        </AutoComplete.Option> );
                                } ) }
                            </AutoComplete.List>
                            <AutoComplete.Empty
                                data-testid={ dataTestId && `${ dataTestId }-empty` }>
                                { emptyMessage ?? t( 'common-autocomplete-empty', 'Nothing found' ) }
                            </AutoComplete.Empty>
                        </AutoComplete.Popup>
                    </AutoComplete.Positioner>
                </AutoComplete.Portal> }
        </AutoComplete.Root>
    );
}

/**
 * A file chooser that uploads nothing.
 *
 * <p><b>The whole point is customUpload.</b> v11's FileUpload will POST the
 * file itself if it is given a url, with its own XHR -- which carries neither
 * the session cookie nor the X-XSRF-TOKEN header, so esback answers 403 to a
 * request that looks perfectly well formed. With customUpload the component
 * does the half it is good at (the hidden input, the dialog, the accept
 * filter) and hands the File to onFile, and the POST goes through esUpload
 * with the rest of the app's writes. Probed in EsPrimeProbe12.
 *
 * <p>`auto` because the alternative is a two-press upload -- choose, then
 * press Upload -- and the second press is a step nobody asked for on a control
 * whose whole job is one press. Single file, because everything this app
 * uploads replaces one thing.
 *
 * <p>The Trigger is the only part assembled: the Content/ItemGroup/Item parts
 * draw a drop zone and a queue of pending files, and with auto on there is
 * never a queue to draw. A caller that wants to show what it uploaded shows
 * the uploaded thing.
 *
 * <p>type="button" on the Trigger for the reason EsSelect's carries it: a v11
 * trigger with no type defaults to submit, and this control is meant to sit in
 * forms.
 *
 * <p>The Trigger renders AS the house Button, so it takes the app's size,
 * severity and variant like any other press; its default is a bare button.
 */
export function EsFileUpload( {
    accept, label, disabled, className, onFile, size, severity, variant,
    'data-testid': dataTestId,
} ) {
    const t = useEsStrings();
    return (
        <FileUpload.Root
            customUpload
            auto
            accept={ accept }
            disabled={ disabled }
            className={ className }
            data-testid={ dataTestId }
            uploadHandler={ ( event ) => {
                const aFile = event?.files?.[ 0 ];

                if ( aFile && onFile ) onFile( aFile );
            } }
        >
            <FileUpload.Trigger as={ Button } type="button" disabled={ disabled }
                                size={ size } severity={ severity } variant={ variant }
                                data-testid={ dataTestId && `${ dataTestId }-trigger` }>
                { label ?? t( 'common-upload', 'Upload' ) }
            </FileUpload.Trigger>
        </FileUpload.Root>
    );
}

/**
 * One handle on a line, across or (orientation="vertical") upright. onChange fires while it
 * moves, onChangeEnd once when it is let go -- which is when a caller that writes to the
 * server should. The handle is `<id>-handle`.
 */
export function EsSlider( {
    value, min = 0, max = 100, step = 1, disabled, className, onChange, onChangeEnd,
    orientation = 'horizontal', 'aria-label': ariaLabel, 'data-testid': dataTestId,
} ) {
    return (
        <Slider.Root value={ value } min={ min } max={ max } step={ step } disabled={ disabled }
                     orientation={ orientation } className={ className } data-testid={ dataTestId }
                     onValueChange={ ( event ) => onChange?.( event.value ) }
                     onValueChangeEnd={ ( event ) => onChangeEnd?.( event.value ) }>
            <Slider.Track>
                <Slider.Range />
            </Slider.Track>
            <Slider.Handle aria-label={ ariaLabel } data-testid={ dataTestId && `${ dataTestId }-handle` } />
        </Slider.Root>
    );
}

/**
 * Two panes side by side, with a grabbable edge between them.
 *
 * <p>Takes `start` and `end` rather than children, because a Splitter with one
 * child or three is not a splitter and there is no reason for a caller to be
 * able to write one. Each panel gets `<id>-start` and `<id>-end`, and the
 * gutter `<id>-gutter`, by the rule at the top of this file for a wrapper that
 * generates what the caller cannot reach.
 *
 * <p><b>The gutter carries no type.</b> It is a div with role="separator" and
 * tabindex=0, not a button -- see EsPrimeProbe12, which pins that, because
 * every other interactive part in this app does carry type="button" and the
 * exception should be one somebody checked rather than one somebody missed.
 *
 * <p>`sizes` is a starting split in percent and not a controlled value: once a
 * reader drags the gutter the component owns the widths, which is the point of
 * having one. Unless the caller passes onResize: then `sizes` is controlled and
 * the caller holds it, which is how EsPanes opens a split at its start pane's
 * measured width -- a width known only after the panels are drawn.
 */
export function EsSplitter( {
    start, end, sizes = [ 50, 50 ], onResize, className, ref, 'data-testid': dataTestId,
} ) {
    const aSizes = onResize
        ? { sizes, onResize: ( pEvent ) => onResize( pEvent.sizes ) }
        : { defaultSizes: sizes };

    return (
        <Splitter.Root ref={ ref } className={ className } { ...aSizes }
                       data-testid={ dataTestId }>
            <Splitter.Panel data-testid={ dataTestId && `${ dataTestId }-start` }>
                { start }
            </Splitter.Panel>
            <Splitter.Gutter className="esSplitLine" data-testid={ dataTestId && `${ dataTestId }-gutter` } />
            <Splitter.Panel data-testid={ dataTestId && `${ dataTestId }-end` }>
                { end }
            </Splitter.Panel>
        </Splitter.Root>
    );
}

/**
 * EsSplitter's line without its panels, for two panes that must stay where they are: the
 * game's screens (EsGameScreens), whose slots never unmount, where a pane moved into a
 * Splitter panel would be rebuilt. The same PrimeReact splitter EsSplitter runs -- its
 * drag, its arrow keys, and its separator role and values -- and the same look
 * (esSplitLine, EsPanes.css).
 *
 * <p>Spread `row` on the panes' parent and `line` on the line between them, and put
 * data-index 0 and 1 on the two panes: the splitter measures the children that carry one.
 * `sizes` is controlled, start then end, in percent; `minSize` is each pane's floor in
 * percent. `onResizeEnd` hears where a drag or a held key stopped.
 * `live` is the split as drawn, mid-drag included.
 */
export function useEsSplitLine( { sizes, minSize = 0, onResizeEnd } ) {
    // The splitter's own end reports the sizes of its last render, which a pointerup or a
    // keyup handled before React drew the last move does not have yet; and a stale end
    // written back as `sizes` would undo the move. So the end is the last move heard.
    const moved = useRef( null );
    const split = useSplitter( {
        sizes,
        panels: [ { minSize }, { minSize } ],
        onResizeStart: () => { moved.current = null; },
        onResize: ( pEvent ) => { moved.current = pEvent.sizes; },
        onResizeEnd: ( pEvent ) => onResizeEnd?.( moved.current ?? pEvent.sizes ),
    } );
    const live = split.panelSizes.length === 2 ? split.panelSizes : sizes;

    return {
        live,
        row: { ref: split.rootProps.ref, 'data-resizing': split.rootProps[ 'data-resizing' ] },
        line: { ...split.getGutterProps( 0 ), className: 'esSplitLine' },
    };
}

/**
 * An RGB picker: a saturation/brightness square, a hue slider, an alpha slider,
 * a swatch, and the hex and four RGBA channels as fields.
 *
 * value and the answers are hex strings -- `#rrggbb`, or `#rrggbbaa` once the
 * alpha is below one -- because that is the one shape a theme colour is saved
 * in. onChange fires on every movement (a live preview); onChangeEnd when a
 * drag lets go or a field is committed, which is when a caller saves.
 *
 * Root draws no element of its own (EsPrimeProbe13), so the wrapper is ours and
 * carries the data-testid; each field is `<id>-<channel>`.
 */
const esHexOfColor = ( pColor ) => ( pColor.getChannelValue( 'alpha' ) < 1
    ? pColor.toString( 'hexa' ) : pColor.toString( 'hex' ) ).toLowerCase();

export function EsInputColor( { value, onChange, onChangeEnd, className, 'data-testid': dataTestId } ) {
    const aParsed = useMemo( () => {
        try {
            return parseColor( value || '#000000' );
        }
        catch {
            return parseColor( '#000000' );
        }
    }, [ value ] );
    const [ color, setColor ] = useState( aParsed );
    useEffect( () => { setColor( aParsed ); }, [ aParsed ] );

    const channel = ( pChannel ) => (
        <InputColor.Input channel={ pChannel } className="esInputColorField" autoComplete="off"
                          aria-label={ pChannel }
                          data-testid={ dataTestId && `${ dataTestId }-${ pChannel }` } />
    );

    return (
        <div className={ 'esInputColor' + ( className ? ' ' + className : '' ) } data-testid={ dataTestId }>
            <InputColor.Root format="rgba" value={ color }
                             onValueChange={ ( pEvent ) => {
                                 setColor( pEvent.value );
                                 onChange?.( esHexOfColor( pEvent.value ) );
                             } }
                             onValueChangeEnd={ ( pEvent ) => onChangeEnd?.( esHexOfColor( pEvent.value ) ) }>
                <InputColor.Area>
                    <InputColor.AreaBackground />
                    <InputColor.AreaHandle />
                </InputColor.Area>
                <div className="esInputColorBar">
                    <div className="esInputColorSliders">
                        <InputColor.Slider>
                            <InputColor.SliderTrack />
                            <InputColor.SliderHandle />
                        </InputColor.Slider>
                        <InputColor.Slider channel="alpha">
                            <InputColor.TransparencyGrid />
                            <InputColor.SliderTrack />
                            <InputColor.SliderHandle />
                        </InputColor.Slider>
                    </div>
                    <InputColor.Swatch>
                        <InputColor.TransparencyGrid />
                        <InputColor.SwatchBackground />
                    </InputColor.Swatch>
                </div>
                <div className="esInputColorFields">
                    { channel( 'hex' ) }
                    { channel( 'red' ) }
                    { channel( 'green' ) }
                    { channel( 'blue' ) }
                    { channel( 'alpha' ) }
                </div>
            </InputColor.Root>
        </div>
    );
}
